// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t341 · EL PANEL DEL ASISTENTE LucIA DEL EQUIPO (21-oct-2026) — REDISEÑADO EN t345 (21-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  UN PANEL LATERAL, NO UNA VENTANA. El dueño: «con animación cuando se abra y cierre el chat, pero sin que salga blur el resto
//  de la web, ya que puedo querer trabajar con ambas cosas a la vez». Así que va pegado a la derecha, de alto completo y con
//  los bordes redondeados, SIN fondo oscuro, sin desenfoque y sin tapar los clics del resto: se trabaja en la agenda o en la
//  ficha con él abierto. No atrapa el foco (no es modal): role="complementary".
//   · El ancho se cambia arrastrando el borde izquierdo (o con las flechas en ese borde), entre 380 y 640 px, y se guarda en
//     el navegador de esa persona (utils/asistenteLucia · ANCHO_PANEL).
//   · «–» lo minimiza a la LucIA de la esquina sin perder nada (ni lo que estabas escribiendo); la «X» lo cierra. Esc cierra.
//   · Entra deslizando desde la derecha con un resorte suave y sale al revés, más rápido; solo transform y opacity. Con
//     «reducir movimiento», solo el desvanecido (utils/asistenteLucia · estiloAnimacion).
//   · En el móvil (< 900 px) ocupa la pantalla entera y entra desde abajo, sin el arrastre del ancho.
//   · Al abrir, el foco va al cuadro de escribir; al cerrar, vuelve al botón de LucIA de la esquina.
//
//  LO QUE PINTA: la cabecera (la cara de LucIA, «Asistente de la clínica» y la foto de la consulta fundida, la de la cabecera de
//  la ficha), el saludo con los ACCESOS RÁPIDOS (utils/asistenteLucia · ACCESOS_RAPIDOS), la conversación separada por días
//  («Hoy», «Ayer», la fecha), y debajo de cada respuesta las TARJETAS con los datos que dio Biango (el paciente, la cita, el
//  presupuesto, el cobro, el informe: backend/services/asistenteLuciaHerramientas.js · fuente.tarjetas), nunca con el texto del
//  modelo, y las tarjetas de lo que LucIA prepara con «Hacerlo» (y, hecha, «Deshacer» durante su rato si la acción lo
//  permite: el registro de acciones, backend/services/asistenteLuciaAcciones.js). Y el micrófono: se graba con la grabadora de la casa
//  (hooks/useGrabadoraNota), el texto transcrito sale en el cuadro para corregirlo y la persona lo manda; el audio no se guarda.
//  Lo que contesta LucIA se pinta como texto (negrita y enlaces a pantallas de la app), nunca como HTML.
//
//  LO QUE TIENES ABIERTO (t345): la ficha de un paciente, una cita, un presupuesto o el día de la agenda (de la dirección y de
//  utils/pantallaAbierta). Solo sus ids viajan al servidor, que los comprueba con la clínica y los permisos; lo comprobado sale
//  en una línea pequeña («Viendo: ficha nº 16938») y LucIA entiende «este paciente» o «esta cita».
//
//  Montado UNA vez en App.jsx detrás del interruptor «asistente-lucia»; se abre con `abrirAsistenteLucia()` (utils/asistenteLucia.js)
//  desde el menú de la foto o desde el botón de LucIA de la esquina (t340).
//
//  t369 · FASE 6, «EN MÁS SITIOS» (21-oct-2026):
//   · DESDE LA FICHA, UNA CITA O UN HUECO DE LA AGENDA (utils/preguntarALucia.jsx): llega con su contexto (`pedido`), que
//     manda sobre lo que hay abierto mientras se siga en esa pantalla, y con las SUGERENCIAS de ese sitio, que da el servidor
//     con los permisos de quien pregunta (GET /sugerencias?donde=…). La «X» lo olvida.
//   · EL ATAJO: Ctrl + Espacio (en un Mac, Opción + L) lo abre con el foco en el cuadro; abierto, si se está escribiendo en
//     él lo cierra (como «–») y si se está trabajando en otra parte de la pantalla lleva a escribir. Solo esa combinación
//     exacta: escribiendo en un campo no salta con nada más (utils/asistenteLucia · esAtajoLucia).
//   · EN EL MÓVIL: a pantalla completa con las zonas seguras; con el teclado abierto, el panel ocupa justo lo que se ve
//     (visualViewport, utils/tecladoMovil) y el cuadro de escribir queda encima del teclado; se cierra deslizando la
//     cabecera hacia abajo (minimiza) o con la X; el micrófono y enviar, grandes para el dedo; las tarjetas, a todo el ancho.
//
//  t452 · AMPLIAR EL PANEL A TODO EL ANCHO (24-oct-2026, utils/asistenteLucia · PANEL_AMPLIADO):
//   · En el ordenador y en la tablet tumbada, debajo de la cara de LucIA, «Ampliar»: el panel ocupa la ventana entera con su
//     margen. La conversación se centra a un ancho de lectura (el saludo, las preguntas, las respuestas, la barra de escribir y
//     la voz) y lo ancho va a todo el ancho que tiene: la gráfica con su tabla al lado, «cómo vamos», lo pendiente y lo de hoy
//     en columnas; las tarjetas pequeñas (un paciente, una cita), en columnas dentro del ancho de lectura.
//   · «Volver al panel» o Esc lo dejan como estaba (ampliado, Esc ya no cierra: vuelve). Ir a una pantalla desde una tarjeta
//     también vuelve al panel, para ver la pantalla con LucIA al lado. Se recuerda por persona en su navegador.
//   · La gráfica se dibuja al ancho que tiene (lo mide el panel con un ResizeObserver) y se vuelve a dibujar al cambiarlo.
//   · En el móvil (< 900 px) el panel ya es la pantalla entera: allí no sale el botón.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useFuncion } from '../../hooks/useFuncion'
import useGrabadoraNota from '../../hooks/useGrabadoraNota'
import { puedeGrabar } from '../../utils/grabacion'
import { confirmar } from '../../context/DialogosContext'
import { Ico } from '../ui'
import FotoPaciente from '../FotoPaciente'
import { abrirWhatsAppPaciente } from '../../utils/abrirWhatsAppPaciente'
import { msgError } from '../../utils/errores'
import { abiertoAhora, suscribirAbierto } from '../../utils/pantallaAbierta'
import { descargarCSV, imprimirInformePDF } from '../../utils/informesExport'
import { useBranding } from '../../hooks/useBranding'
import { TarjetaCadena, TarjetaAvisosDia, AvisosDiaPie, esSaludoDelDia, TarjetaComparativa } from './TarjetasLuciaMasLista'   // t368 · la fase 5 («más lista»): varias cosas a la vez, lo de hoy y cómo vamos
import { TarjetaSerie } from '../../utils/tarjetaSerieLucia'   // t374 · la fase 7c: una cifra de analizar, con su gráfica, su tabla, PDF y Excel
import TarjetaVideoFormacion from './TarjetaVideoFormacion'   // t591 · el vídeo de la Formación de «cómo se hace», con su reproductor
import {
  EVENTO_ABRIR_ASISTENTE_LUCIA, EVENTO_CERRAR_ASISTENTE_LUCIA, AVISO_DEL_BOTON_DE_LUCIA, IMAGEN_LUCIA, RECORTE_CARA_LUCIA, lineasDeRespuesta,
  SALUDO_LUCIA, LEMA_LUCIA, ACCESOS_RAPIDOS, ANCHO_PANEL, anchoValido, leerAncho, guardarAncho, ANIMACION_PANEL, estiloAnimacion,
  prefiereMenosMovimiento, etiquetaDia, inicialesDe, pantallaDeLaDireccion, pantallaParaLucia, clavePantalla,
  contextoLimpio, claveDeLaPagina, pantallaConPedido, SITIOS_LUCIA, PISTA_POR_SITIO, esMac, esAtajoLucia, textoAtajoLucia, gestoCierra,
  respuestaCortaLucia, propuestaQueEspera, textoParaLeer, leerEnVoz,
  PANEL_AMPLIADO, leerAmpliado, guardarAmpliado, anchoConversacionCalculado, anchoDeLasTarjetas, columnasQueCaben, TARJETAS_ANCHAS,
} from '../../utils/asistenteLucia'   // (t430: «respuestaCortaLucia» y las tres de después, el «sí» dictado y la propuesta leída en voz alta; t452: la última línea, ampliar el panel)
import { huecoDelTeclado } from '../../utils/tecladoMovil'
import { avisarDelEnlace } from '../../utils/filtroDeLaUrl'   // t385 · la pantalla ya abierta vuelve a leer el día o el filtro del «Ver»
import { t as i18nT, tx, marca, tDato } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const BASE = '/asistente-lucia'
const ventana = () => (typeof window !== 'undefined' ? window : null)
const zoomDeLaPagina = () => { try { return parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { return 1 } }
const SIN_TECLADO = Object.freeze({ arriba: 0, abajo: 0 })
const PISTA_DE_SIEMPRE = marca('components.asistenteLucia.PanelAsistenteLucia.escribe_tu_consulta_2', 'Escribe tu consulta…')
const almacen = () => { try { const w = ventana(); return w && w.localStorage ? w.localStorage : null } catch (_) { return null } }
const horaAhora = () => { try { const d = new Date(); return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') } catch (_) { return '' } }
const alSiguienteCuadro = (fn) => { const w = ventana(); if (w && typeof w.requestAnimationFrame === 'function') w.requestAnimationFrame(() => fn()); else setTimeout(fn, 16) }

// LA CARA DE LucIA en un círculo (la misma imagen quieta del botón de la esquina, recortada a la cabeza).
function CaraLucia({ lado = 36, borde = true }) {
  return (
    <div data-asistente-cara="" aria-hidden="true" style={{ width: lado + 'px', height: lado + 'px', borderRadius: '50%', overflow: 'hidden', position: 'relative', flexShrink: 0,
      border: borde ? '1px solid var(--border)' : 'none', background: 'color-mix(in srgb, var(--primary) 10%, var(--surface))' }}>
      <img src={IMAGEN_LUCIA} alt="" draggable="false" style={{ position: 'absolute', maxWidth: 'none', display: 'block', ...RECORTE_CARA_LUCIA }} />
    </div>
  )
}

function Texto({ texto, onEnlace }) {
  const lineas = lineasDeRespuesta(texto)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {lineas.map((l, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', minHeight: l.piezas.length ? undefined : '6px' }}>
          {l.lista && <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>·</span>}
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {l.piezas.map((p, j) => (p.t === 'negrita'
              ? <b key={j}>{p.v}</b>
              : p.t === 'enlace'
                ? <a key={j} href={p.ruta} onClick={(e) => { e.preventDefault(); onEnlace(p.ruta) }} style={{ color: 'var(--primary)', fontWeight: 600 }}>{p.v}</a>
                : <span key={j}>{p.v}</span>))}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── LAS TARJETAS: con los DATOS de la herramienta (fuente.tarjetas), el mismo patrón para todas ─────────────────────
// En un panel estrecho, el botón «Ver…» baja debajo de los datos (flexWrap) en vez de apretarlos.
const cajaTarjeta = { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '11px', border: '1px solid var(--border)', borderRadius: '12px', padding: '10px 11px', background: 'var(--surface)' }
const botonVer = { flexShrink: 0, marginLeft: 'auto', border: 'none', background: 'color-mix(in srgb, var(--primary) 12%, var(--surface))', color: 'var(--primary)', borderRadius: '9px', padding: '6px 10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }
const lineaMenor = { display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }

// t452 · LA COLUMNA DE LECTURA del panel ampliado: a todo el ancho hasta `lectura` px, centrada.
const enLaColumna = (lectura) => ({ width: '100%', maxWidth: lectura + 'px', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box', minWidth: 0 })
// Lo que va en la columna de lectura (el saludo, los accesos, una pregunta, lo preparado): sin `lectura`, tal cual.
function EnColumna({ lectura, children }) {
  if (!lectura) return children
  return <div data-asistente-columna="" style={{ ...enLaColumna(lectura), display: 'flex', flexDirection: 'column', gap: '12px' }}>{children}</div>
}

function BotonVer({ texto, ruta, onEnlace }) {
  if (!ruta) return null
  return <button type="button" data-asistente-ver="" onClick={() => onEnlace(ruta)} style={botonVer}>{texto} <Ico n="externo" s={12} /></button>
}
function IconoTarjeta({ icono, color = 'var(--primary)' }) {
  return (
    <span aria-hidden="true" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'color-mix(in srgb, ' + color + ' 13%, var(--surface))', color }}>
      <Ico n={icono} s={19} />
    </span>
  )
}

function TarjetaPaciente({ t, onEnlace, onElegir, ocupado }) {
  return (
    <div data-asistente-tarjeta="paciente" style={cajaTarjeta}>
      {t.tiene_foto
        ? <FotoPaciente pacienteId={t.id} nombre={t.nombre} size={44} tieneFoto={t.tiene_foto} noQuiereFoto={t.foto_no_deseada === true} mini />
        : <span data-asistente-iniciales="" aria-hidden="true" style={{ width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in srgb, var(--primary) 14%, var(--surface))', color: 'var(--primary)', fontWeight: 800, fontSize: '15px' }}>{inicialesDe(t.nombre)}</span>}
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '13.5px', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{t.nombre}</div>
        <div style={lineaMenor}>
          {t.num_historia && <span>{tx('components.asistenteLucia.PanelAsistenteLucia.n', 'nº {{num_historia}}', { num_historia: t.num_historia })}</span>}
          {t.nacimiento && <span style={{ whiteSpace: 'nowrap' }}>{t.nacimiento}{t.edad != null ? ' (' + t.edad + (t.edad === 1 ? ' año)' : ' años)') : ''}</span>}
        </div>
        {(t.telefono || t.poblacion) && (
          <div style={lineaMenor}>
            {t.telefono && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Ico n="phone" s={12} /> {t.telefono}</span>}
            {t.poblacion && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Ico n="pin" s={12} /> {t.poblacion}</span>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', marginLeft: 'auto' }}>
        <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.ver_ficha', 'Ver ficha')} ruta={t.ruta} onEnlace={onEnlace} />
        {onElegir && <button type="button" data-asistente-elegir="" disabled={ocupado} onClick={() => onElegir(t)}
          style={{ ...botonVer, background: 'transparent', border: '1px solid var(--primary)', cursor: ocupado ? 'default' : 'pointer' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.es_este', 'Es este')}</button>}
      </div>
    </div>
  )
}

function TarjetaCita({ t, onEnlace }) {
  return (
    <div data-asistente-tarjeta="cita" style={cajaTarjeta}>
      <IconoTarjeta icono="calendar" color="var(--warning, #d97706)" />
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{t.tratamiento || i18nT('components.asistenteLucia.PanelAsistenteLucia.cita', 'Cita')}</div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {[t.fecha, t.hora].filter(Boolean).join(' · ')}
        </div>
        <div style={lineaMenor}>
          {t.profesional && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Ico n="user" s={12} /> {t.profesional}</span>}
          {t.paciente && <span>{t.paciente}</span>}
          {t.estado && <span>{t.estado}</span>}
        </div>
      </div>
      <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.ver_cita', 'Ver cita')} ruta={t.ruta} onEnlace={onEnlace} />
    </div>
  )
}

function TarjetaPresupuesto({ t, onEnlace }) {
  return (
    <div data-asistente-tarjeta="presupuesto" style={cajaTarjeta}>
      <IconoTarjeta icono="presupuestos" />
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{t.titulo}{t.numero ? <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}> · {t.numero}</span> : null}</div>
        {t.importe && <div style={{ fontSize: '14px', fontWeight: 800, marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>{t.importe}</div>}
        <div style={lineaMenor}>
          {t.estado && <span>{t.estado}</span>}
          {t.fecha && <span>{t.fecha}</span>}
          {t.paciente && <span>{t.paciente}</span>}
        </div>
      </div>
      <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.ver_presupuesto', 'Ver presupuesto')} ruta={t.ruta} onEnlace={onEnlace} />
    </div>
  )
}

function TarjetaCobro({ t, onEnlace }) {
  return (
    <div data-asistente-tarjeta="cobro" style={cajaTarjeta}>
      <IconoTarjeta icono="cash" color="var(--success, #15803d)" />
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', fontWeight: 600 }}>{t.titulo}</div>
        <div style={{ fontSize: '18px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{t.importe}</div>
        {(t.detalle || []).map((d, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span>{d.etiqueta}</span><b style={{ fontVariantNumeric: 'tabular-nums' }}>{d.valor}</b>
          </div>
        ))}
        {t.pie && <div style={{ ...lineaMenor, marginTop: '5px' }}>{t.pie}</div>}
      </div>
      <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.ver', 'Ver')} ruta={t.ruta} onEnlace={onEnlace} />
    </div>
  )
}

// El informe: su total y, cuando la pantalla de Informes lo permite, su descarga (t345, punto 5: `descargas`).
function TarjetaInforme({ t, onEnlace, onDescargar }) {
  return (
    <div data-asistente-tarjeta="informe" style={cajaTarjeta}>
      <IconoTarjeta icono="informes" />
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{t.titulo}</div>
        <div style={lineaMenor}>
          {t.periodo && <span>{t.periodo}</span>}
          {t.filas != null && <span>{t.filas} {t.filas === 1 ? i18nT('components.asistenteLucia.PanelAsistenteLucia.fila', 'fila') : i18nT('components.asistenteLucia.PanelAsistenteLucia.filas', 'filas')}</span>}
        </div>
        {t.total && <div style={{ fontSize: '13px', marginTop: '3px' }}>{t.total.etiqueta}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{t.total.valor}</b></div>}
        {Array.isArray(t.descargas) && t.descargas.length > 0 && onDescargar && (
          <div style={{ display: 'flex', gap: '6px', marginTop: '7px', flexWrap: 'wrap' }}>
            {t.descargas.map((d) => (
              <button key={d.formato} type="button" data-asistente-descargar={d.formato} onClick={() => onDescargar(t, d)}
                style={{ ...botonVer, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                <Ico n="download" s={12} /> {d.texto}
              </button>
            ))}
          </div>
        )}
      </div>
      <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.abrir', 'Abrir')} ruta={t.ruta} onEnlace={onEnlace} />
    </div>
  )
}

// t505 · B-2240 · UN INFORME A MEDIDA (la herramienta informe_a_medida: el mismo motor que el Informe avanzado). Lo entendido en
// llano (para ver si es eso), cuántos, las primeras filas, lo que ha tomado de lo ambiguo y sus botones: «Descargar Excel» (lo saca
// otra vez el servidor y lo apunta en el Registro de actividad), «Abrir en el Informe avanzado» (con los mismos filtros) y, si es
// una lista de pacientes y se puede, «Usar esta lista en una campaña» (lo prepara el servidor: una tarjeta con «Hacerlo» debajo).
const claveCampana = (t) => { try { return JSON.stringify(t && t.peticion) } catch (_) { return '' } }
const celdaTabla = { padding: '5px 7px', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }
function valorCelda(v, c) {
  if (v == null || v === '') return ''
  if (c && c.tipo === 'num' && /€/.test(c.label || '')) { try { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(v)) } catch (_) { return String(v) } }
  return String(v)
}
// Sin estado propio (como la de WhatsApp): lo de «Preparando…» y su error lo lleva el panel (`estadoCampana`, por tarjeta).
function TarjetaInformeMedida({ t, onEnlace, onDescargar, onCampana, ocupado, estadoCampana }) {
  const campana = (estadoCampana && estadoCampana[claveCampana(t)]) || { pidiendo: false, error: '' }
  const columnas = Array.isArray(t.columnas) ? t.columnas : []
  const filas = Array.isArray(t.filas) ? t.filas : []
  const usar = () => { if (onCampana && !campana.pidiendo) onCampana(t) }
  return (
    <div data-asistente-tarjeta="informe_medida" style={{ ...cajaTarjeta, flexDirection: 'column', alignItems: 'stretch', gap: '8px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
        <IconoTarjeta icono="informes" />
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{t.titulo}</div>
          {t.total != null && <div style={{ fontSize: '13px', marginTop: '2px' }}><b style={{ fontVariantNumeric: 'tabular-nums', fontSize: '15px' }}>{Number(t.total).toLocaleString('es-ES')}</b> {t.unidad || i18nT('components.asistenteLucia.PanelAsistenteLucia.filas', 'filas')}</div>}
        </div>
      </div>
      {t.entendido && <div data-informe-entendido="" style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5, overflowWrap: 'anywhere' }}><b style={{ color: 'var(--text-primary)' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.he_entendido', 'He entendido:')}</b> {t.entendido}</div>}
      {(Array.isArray(t.avisos) ? t.avisos : []).map((v, i) => <div key={i} role="note" style={{ ...lineaMenor, color: 'var(--warning, #b45309)', display: 'block', overflowWrap: 'anywhere' }}>{tx('components.asistenteLucia.PanelAsistenteLucia.si_es_otra_cosa_dimelo', '{{v}}. Si es otra cosa, dímelo o cámbialo en el Informe avanzado.', { v })}</div>)}
      {filas.length > 0 && columnas.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '9px', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 0 }}>
            <thead><tr>{columnas.map((c) => <th key={c.key} style={{ ...celdaTabla, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-2)' }}>{c.label}</th>)}</tr></thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={i}>
                  {columnas.map((c) => (
                    <td key={c.key} style={{ ...celdaTabla, fontVariantNumeric: c.tipo === 'num' ? 'tabular-nums' : undefined }}>
                      {c.key === t.col_paciente && f._paciente_id
                        ? <a href={'/pacientes?paciente=' + f._paciente_id} onClick={(e) => { e.preventDefault(); onEnlace('/pacientes?paciente=' + f._paciente_id) }} style={{ color: 'var(--primary)', fontWeight: 600 }}>{valorCelda(f[c.key], c)}</a>
                        : valorCelda(f[c.key], c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {t.total > filas.length && filas.length > 0 && <div style={lineaMenor}>{tx('components.asistenteLucia.PanelAsistenteLucia.van_las_primeras_el_excel', 'Van las {{length}} primeras: el Excel las lleva todas.', { length: filas.length })}</div>}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {Array.isArray(t.descargas) && onDescargar && t.descargas.map((d) => (
          <button key={d.formato} type="button" data-asistente-descargar={d.formato} onClick={() => onDescargar(t, d)} disabled={!t.total}
            style={{ ...botonVer, marginLeft: 0, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
            <Ico n="download" s={12} /> {d.texto}
          </button>
        ))}
        {t.ruta && <button type="button" data-asistente-abrir-avanzado="" onClick={() => onEnlace(t.ruta)} style={{ ...botonVer, marginLeft: 0 }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.abrir_en_el_informe_avanzado', 'Abrir en el Informe avanzado ')}<Ico n="externo" s={12} /></button>}
        {t.campana && onCampana && (
          <button type="button" data-asistente-campana="" disabled={ocupado || campana.pidiendo} onClick={usar} style={{ ...botonVer, marginLeft: 0, cursor: campana.pidiendo ? 'wait' : 'pointer' }}>
            {campana.pidiendo ? i18nT('components.asistenteLucia.PanelAsistenteLucia.preparando', 'Preparando…') : i18nT('components.asistenteLucia.PanelAsistenteLucia.usar_esta_lista_en_una', 'Usar esta lista en una campaña')}
          </button>
        )}
      </div>
      {campana.error && <div role="alert" style={{ ...lineaMenor, color: 'var(--danger)' }}>{campana.error}</div>}
    </div>
  )
}

// t349 · «ABRIR SU WHATSAPP» (la herramienta abrir_conversacion de la tanda 2): la MISMA función que todos los botones de
// WhatsApp de un paciente (utils/abrirWhatsAppPaciente → POST /api/conversaciones/abrir, que la busca o la crea sin reabrirla).
// LucIA no escribe nada: la abre la persona al pulsar, y se navega a Comunicación sin cerrar el panel. Si no se puede, el porqué.
// Sin estado propio (como los accesos rápidos): lo lleva el panel (`onWhatsapp` y `estadoWhatsapp`, por paciente).
function TarjetaWhatsapp({ t, onWhatsapp, estadoWhatsapp }) {
  const estado = (estadoWhatsapp && estadoWhatsapp[t.paciente_id || t.id]) || { abriendo: false, motivo: '' }
  const abrir = () => { if (!estado.abriendo && onWhatsapp) onWhatsapp(t) }
  return (
    <div data-asistente-tarjeta="whatsapp" style={cajaTarjeta}>
      <IconoTarjeta icono="whatsapp" color="var(--success, #15803d)" />
      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{t.nombre}</div>
        {t.num_historia && <div style={lineaMenor}><span>{tx('components.asistenteLucia.PanelAsistenteLucia.n', 'nº {{num_historia}}', { num_historia: t.num_historia })}</span></div>}
        {(Array.isArray(t.avisos) ? t.avisos : []).map((v, i) => <div key={i} role="note" style={{ ...lineaMenor, color: 'var(--warning, #b45309)' }}>{v}</div>)}
        {estado.motivo && <div role="alert" style={{ ...lineaMenor, color: 'var(--danger)' }}>{estado.motivo}</div>}
      </div>
      <button type="button" data-asistente-abrir-whatsapp="" disabled={estado.abriendo} onClick={abrir} style={{ ...botonVer, cursor: estado.abriendo ? 'wait' : 'pointer' }}>
        {estado.abriendo ? i18nT('components.asistenteLucia.PanelAsistenteLucia.abriendo', 'Abriendo…') : i18nT('components.asistenteLucia.PanelAsistenteLucia.abrir_su_whatsapp', 'Abrir su WhatsApp')} <Ico n="externo" s={12} />
      </button>
    </div>
  )
}

// t345 (21-oct) · LO PENDIENTE (la herramienta `pendientes`, el acceso «Tareas pendientes»): cada cosa con cuántas hay, las
// primeras y su pantalla; lo que esta persona no puede ver, dicho.
function TarjetaPendientes({ t, onEnlace }) {
  return (
    <div data-asistente-tarjeta="pendientes" style={{ ...cajaTarjeta, flexDirection: 'column', alignItems: 'stretch', gap: '8px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <IconoTarjeta icono="bell" />
        <div style={{ fontWeight: 800, fontSize: '13.5px' }}>{t.titulo}</div>
      </div>
      {/* t452 · cada cosa en su columna cuando hay sitio (el panel ampliado); en el panel de siempre, una debajo de otra. */}
      <div data-asistente-columnas="" style={columnasQueCaben(260)}>
        {(t.grupos || []).map((g) => (
          <div key={g.clave} data-pendiente={g.clave} style={{ borderTop: '1px solid var(--border)', paddingTop: '7px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: '12.5px', fontWeight: 700 }}>{g.titulo}</span>
              <b style={{ fontVariantNumeric: 'tabular-nums', fontSize: '15px' }}>{g.sin_permiso || g.no_disponible ? '—' : g.n}</b>
              {!g.sin_permiso && !g.no_disponible && <BotonVer texto={i18nT('components.asistenteLucia.PanelAsistenteLucia.ver', 'Ver')} ruta={g.ruta} onEnlace={onEnlace} />}
            </div>
            {g.sin_permiso
              ? <div style={lineaMenor}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.tu_usuario_no_tiene_permiso', 'Tu usuario no tiene permiso para verlo.')}</div>
              : g.no_disponible
                ? <div style={lineaMenor}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.ahora_no_se_ha_podido', 'Ahora no se ha podido leer.')}</div>
                : (g.primeros || []).map((p, i) => <div key={i} style={{ ...lineaMenor, display: 'block', overflowWrap: 'anywhere' }}>{p}</div>)}
          </div>
        ))}
      </div>
    </div>
  )
}

const TARJETAS = { paciente: TarjetaPaciente, cita: TarjetaCita, presupuesto: TarjetaPresupuesto, cobro: TarjetaCobro, informe: TarjetaInforme, whatsapp: TarjetaWhatsapp, pendientes: TarjetaPendientes,
  informe_medida: TarjetaInformeMedida,   // t505 · los informes a medida
  avisos_dia: TarjetaAvisosDia, comparativa: TarjetaComparativa,   // t368 · lo de hoy y cómo vamos
  serie: TarjetaSerie,   // t374 · las cifras de analizar
  video_formacion: TarjetaVideoFormacion }   // t591 · el vídeo de la Formación adjunto a «cómo se hace»

// Lo que ha dado Biango para esa respuesta: sus TARJETAS (en lista) o, si no trae, sus cifras; y su enlace y sus artículos.
// t452 · `ancho`: lo que tienen las tarjetas (la gráfica se dibuja a esa medida). `lectura` (solo con el panel AMPLIADO): el ancho
// de lectura; entonces las tarjetas anchas (TARJETAS_ANCHAS) van a todo el ancho y las demás, en columnas dentro de la lectura.
const cajaColumnasDeLectura = (lectura) => ({ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, ' + lectura + 'px) minmax(0, 1fr)', rowGap: '8px', marginTop: '8px', fontSize: '12.5px', minWidth: 0 })
const enLectura = { gridColumn: '2', minWidth: 0 }
const aTodoElAncho = { gridColumn: '1 / -1', minWidth: 0 }
// Las tarjetas seguidas que no son anchas, juntas en un grupo (para ponerlas en columnas); cada ancha, sola.
function gruposDeTarjetas(tarjetas) {
  const out = []
  for (const t of tarjetas) {
    const ancha = TARJETAS_ANCHAS.includes(t.tipo)
    const ultimo = out[out.length - 1]
    if (!ancha && ultimo && !ultimo.ancha) ultimo.tarjetas.push(t)
    else out.push({ ancha, tarjetas: [t] })
  }
  return out
}
function Fuente({ f, onEnlace, onPregunta, onDescargar, onCampana, estadoCampana, ocupado, onWhatsapp, estadoWhatsapp, ancho = null, lectura = null }) {
  if (!f) return null
  const tarjetas = (Array.isArray(f.tarjetas) ? f.tarjetas : []).filter((t) => t && TARJETAS[t.tipo])
  const elegir = f.elegir ? (t) => onPregunta('Me refiero a ' + t.nombre + (t.num_historia ? ' (nº de historia ' + t.num_historia + ')' : '')) : null
  const pintar = (t, i, anchoDeEsta) => { const T = TARJETAS[t.tipo]; return <T key={t.tipo + (t.id || i)} t={t} onEnlace={onEnlace} onElegir={t.tipo === 'paciente' ? elegir : null} onDescargar={onDescargar} onCampana={onCampana} estadoCampana={estadoCampana} ocupado={ocupado} onWhatsapp={onWhatsapp} estadoWhatsapp={estadoWhatsapp} ancho={anchoDeEsta} /> }
  // En columnas de lectura (ampliado) o en lista (el panel de siempre y el móvil).
  const amplio = Number(lectura) > 0
  const ponerEn = (sitio, hijo, clave) => (amplio ? <div key={clave} data-asistente-sitio={sitio === aTodoElAncho ? 'ancho' : 'lectura'} style={sitio}>{hijo}</div> : hijo)
  return (
    <div data-asistente-fuente="" data-asistente-ampliada={amplio ? '' : undefined}
      style={amplio ? cajaColumnasDeLectura(lectura) : { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
      {tarjetas.length > 0
        ? (amplio
          ? gruposDeTarjetas(tarjetas).map((g, k) => (g.ancha
            ? ponerEn(aTodoElAncho, pintar(g.tarjetas[0], k, ancho), 'g' + k)
            : ponerEn({ ...columnasQueCaben(300), ...enLectura, columnGap: '10px', rowGap: '8px' }, g.tarjetas.map((t, i) => pintar(t, i, null)), 'g' + k)))
          : tarjetas.map((t, i) => pintar(t, i, ancho)))
        : ponerEn(enLectura, (
          <div style={{ ...cajaTarjeta, display: 'block' }}>
            {f.titulo && <div style={{ fontWeight: 700, marginBottom: '5px', color: 'var(--text-primary)' }}>{f.titulo}</div>}
            {Array.isArray(f.cifras) && f.cifras.length > 0 && (
              <div style={{ ...columnasQueCaben(240), columnGap: '22px', rowGap: '2px' }}>
                {f.cifras.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', color: c.menor ? 'var(--text-muted)' : 'var(--text-primary)', fontSize: c.menor ? '12px' : '12.5px' }}>
                    <span>{String(c.etiqueta || '').trim()}</span>
                    <b style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{c.valor}</b>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(f.pacientes) && f.pacientes.length > 0 && (
              <div style={{ ...columnasQueCaben(260), rowGap: '4px', marginTop: '4px' }}>
                {f.pacientes.map((p) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span>{p.nombre}{p.num_historia ? <span style={{ color: 'var(--text-muted)' }}>{tx('components.asistenteLucia.PanelAsistenteLucia.n_2', ' · nº {{num_historia}}', { num_historia: p.num_historia })}</span> : null}</span>
                    <span style={{ display: 'flex', gap: '6px' }}>
                      {f.elegir && <button type="button" disabled={ocupado} onClick={() => onPregunta('Me refiero a ' + p.nombre + (p.num_historia ? ' (nº de historia ' + p.num_historia + ')' : ''))}
                        style={{ border: '1px solid var(--primary)', color: 'var(--primary)', background: 'transparent', borderRadius: '7px', padding: '2px 8px', fontSize: '11.5px', fontWeight: 700, cursor: ocupado ? 'default' : 'pointer' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.este', 'Este')}</button>}
                      <a href={p.ruta} onClick={(e) => { e.preventDefault(); onEnlace(p.ruta) }} style={{ color: 'var(--primary)', fontSize: '11.5px', fontWeight: 600 }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.ficha', 'Ficha')}</a>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ), 'cifras')}
      {Array.isArray(f.articulos) && f.articulos.length > 0 && ponerEn(enLectura, (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {f.articulos.map((t) => (
            <a key={t} href="#" onClick={(e) => { e.preventDefault(); try { window.dispatchEvent(new CustomEvent('abrir-ayuda', { detail: { titulo: t } })) } catch (_) { /* sin ventana */ } }}
              style={{ color: 'var(--primary)', fontWeight: 600 }}>{t}</a>
          ))}
        </div>
      ), 'articulos')}
      {f.enlace && f.enlace.ruta && ponerEn(enLectura, (
        <a href={f.enlace.ruta} onClick={(e) => { e.preventDefault(); onEnlace(f.enlace.ruta) }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--primary)', fontWeight: 700 }}>
          {f.enlace.texto || i18nT('components.asistenteLucia.PanelAsistenteLucia.abrir', 'Abrir')} <Ico n="chevronR" s={13} />
        </a>
      ), 'enlace')}
    </div>
  )
}

// Lo que LucIA ha PREPARADO: qué va a pasar, sus avisos y «Hacerlo». Hecha, el enlace a lo creado; caducada o descartada, ya no.
function TarjetaAccion({ a, ocupado, fallo, onHacer, onDescartar, onDeshacer, onEnlace, comprobado = false, onComprobar, onOfrecer }) {
  // t368 · varias cosas preparadas en una misma respuesta: UNA tarjeta con sus pasos en orden (components/asistenteLucia/TarjetasLuciaMasLista).
  if (a && a.tarjeta && Array.isArray(a.tarjeta.pasos)) return <TarjetaCadena a={a} ocupado={ocupado} fallo={fallo} onHacer={onHacer} onDescartar={onDescartar} onDeshacer={onDeshacer} onEnlace={onEnlace} comprobado={comprobado} onComprobar={onComprobar} />
  const t = a.tarjeta || {}
  const estado = a.estado || 'propuesta'
  // t351 · EL CANDADO DE UNA FACTURA (backend/services/asistenteLuciaFactura): el aviso de que una factura emitida no se borra
  // ni se cambia y la casilla «He comprobado los datos». Sin marcarla, «Hacerlo» no se puede pulsar; marcada, viaja con
  // «Hacerlo» (`comprobado: true`) y el servidor tampoco emite sin ella. Sin estado propio: la casilla la lleva el panel, por acción.
  const candado = t.candado && typeof t.candado === 'object' ? t.candado : null
  return (
    <div data-asistente-accion={estado} style={{ ...cajaTarjeta, display: 'block', marginTop: '8px', border: '1.5px solid ' + (estado === 'propuesta' ? 'var(--primary)' : 'var(--border)'), fontSize: '12.5px' }}>
      <div style={{ fontWeight: 800, marginBottom: '6px' }}>{t.titulo || i18nT('components.asistenteLucia.PanelAsistenteLucia.preparado', 'Preparado')}</div>
      {(t.lineas || []).map((l, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <span style={{ color: 'var(--text-muted)' }}>{l.etiqueta}</span><b style={{ textAlign: 'right' }}>{l.valor}</b>
        </div>
      ))}
      {/* t349 · el TEXTO EXACTO de lo que se va a mandar (un WhatsApp): tal cual, con sus saltos de línea y sin pasar por el modelo. */}
      {t.texto && (
        <div data-asistente-texto-exacto="" style={{ marginTop: '7px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'color-mix(in srgb, var(--success, #15803d) 8%, var(--surface))',
          border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 10px', color: 'var(--text-primary)' }}>{t.texto}</div>
      )}
      {(t.avisos || []).map((v, i) => <div key={'v' + i} role="note" style={{ marginTop: '6px', color: 'var(--warning, #b45309)' }}>{v}</div>)}
      {/* t430 · ANTES DE «HACERLO», SI SE PODRÁ DESHACER (y cuánto rato) o que no: lo pone el servidor (backend/services/asistenteLuciaAcciones · conLoQueDiceAntes). */}
      {estado === 'propuesta' && t.deshacer_aviso && t.deshacer_aviso.texto && (
        <div data-asistente-deshacer-aviso={t.deshacer_aviso.se_puede ? 'si' : 'no'} role="note"
          style={{ marginTop: '6px', fontSize: '12px', color: t.deshacer_aviso.se_puede ? 'var(--text-muted)' : 'var(--warning, #b45309)', fontWeight: t.deshacer_aviso.se_puede ? 500 : 700 }}>
          {t.deshacer_aviso.texto}
        </div>
      )}
      {/* t351 · LO QUE SE HACE DETRÁS (el anticipo con su factura de anticipo): su vista previa entera, debajo, con los DATOS del servidor. */}
      {t.factura && typeof t.factura === 'object' && (
        <div data-asistente-factura-detras="" style={{ marginTop: '9px', paddingTop: '8px', borderTop: '1px dashed var(--border)' }}>
          <div style={{ fontWeight: 800, marginBottom: '5px' }}>{t.factura.titulo || i18nT('components.asistenteLucia.PanelAsistenteLucia.y_su_factura', 'Y su factura')}</div>
          {(t.factura.lineas || []).map((l, i) => (
            <div key={'f' + i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ color: 'var(--text-muted)' }}>{l.etiqueta}</span><b style={{ textAlign: 'right' }}>{l.valor}</b>
            </div>
          ))}
          {(t.factura.avisos || []).map((v, i) => <div key={'fv' + i} role="note" style={{ marginTop: '5px', color: 'var(--warning, #b45309)' }}>{v}</div>)}
        </div>
      )}
      {estado === 'propuesta' && t.despues && <div style={{ marginTop: '6px', color: 'var(--text-muted)', fontSize: '12px' }}>{t.despues}</div>}
      {estado === 'propuesta' && candado && (
        <div role="note" data-asistente-candado="" style={{ marginTop: '8px', padding: '8px 10px', border: '1px solid var(--danger)', borderRadius: '9px', background: 'color-mix(in srgb, var(--danger) 7%, var(--surface))', color: 'var(--text-primary)', fontWeight: 700 }}>
          {candado.aviso}
          <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '7px', fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" data-asistente-comprobado="" checked={comprobado} disabled={ocupado} onChange={(e) => onComprobar && onComprobar(a.id, e.target.checked)} />
            {candado.casilla || i18nT('components.asistenteLucia.PanelAsistenteLucia.he_comprobado_los_datos', 'He comprobado los datos')}
          </label>
        </div>
      )}
      {estado === 'propuesta' && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '9px' }}>
          <button type="button" data-asistente-hacer="" disabled={ocupado || (!!candado && !comprobado)} onClick={() => onHacer(a, candado ? { comprobado: true } : {})}
            style={{ border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', borderRadius: '9px', padding: '7px 14px', fontWeight: 800, cursor: ocupado ? 'wait' : ((!!candado && !comprobado) ? 'not-allowed' : 'pointer'), opacity: (!!candado && !comprobado) ? 0.55 : 1 }}>
            {ocupado ? i18nT('components.asistenteLucia.PanelAsistenteLucia.haciendolo', 'Haciéndolo…') : (t.boton || i18nT('components.asistenteLucia.PanelAsistenteLucia.hacerlo', 'Hacerlo'))}
          </button>
          {/* t370 · fase 7b · «Ofrecérselo al paciente»: lo enseña el servidor (`tarjeta.ofrecer`) en la de un plan o una cita. */}
          {t.ofrecer && onOfrecer && (
            <button type="button" data-asistente-ofrecer="" disabled={ocupado} onClick={() => onOfrecer(a)}
              style={{ border: '1px solid var(--primary)', background: 'var(--surface)', color: 'var(--primary)', borderRadius: '9px', padding: '7px 12px', fontWeight: 800, cursor: ocupado ? 'wait' : 'pointer' }}>
              {t.ofrecer.boton || i18nT('components.asistenteLucia.PanelAsistenteLucia.ofrecerselo_al_paciente', 'Ofrecérselo al paciente')}
            </button>
          )}
          <button type="button" data-asistente-descartar="" disabled={ocupado} onClick={() => onDescartar(a)}
            style={{ border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', borderRadius: '9px', padding: '7px 12px', fontWeight: 700, cursor: 'pointer' }}>
            {i18nT('components.asistenteLucia.PanelAsistenteLucia.no_gracias', 'No, gracias')}
          </button>
        </div>
      )}
      {estado === 'hecha' && (
        <div style={{ marginTop: '8px', color: 'var(--success, #15803d)', fontWeight: 700 }}>
          {(a.resultado && a.resultado.texto) || i18nT('components.asistenteLucia.PanelAsistenteLucia.hecho', 'Hecho.')}
          {a.resultado && a.resultado.ruta && (
            <a href={a.resultado.ruta} onClick={(e) => { e.preventDefault(); onEnlace(a.resultado.ruta) }} style={{ marginLeft: '6px', color: 'var(--primary)' }}>{a.resultado.enlace_texto || i18nT('components.asistenteLucia.PanelAsistenteLucia.abrir_la_ficha', 'Abrir la ficha')}</a>
          )}
        </div>
      )}
      {estado === 'hecha' && a.resultado && (a.resultado.avisos || []).map((v, i) => <div key={'r' + i} role="note" style={{ marginTop: '4px', color: 'var(--warning, #b45309)' }}>{v}</div>)}
      {/* t477 · comprobado después de hacerlo (backend/services/asistenteLuciaComprobar.js): lo que no ha quedado como se pidió, claro */}
      {estado === 'hecha' && a.resultado && a.resultado.no_coincide && <div role="alert" data-asistente-no-coincide="" style={{ marginTop: '6px', color: 'var(--danger)', fontWeight: 700 }}>{a.resultado.no_coincide}</div>}
      {estado === 'hecha' && a.deshacer && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <button type="button" data-asistente-deshacer="" disabled={ocupado} onClick={() => onDeshacer(a)}
            style={{ border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '5px 11px', fontWeight: 700, cursor: ocupado ? 'wait' : 'pointer' }}>
            {ocupado ? i18nT('components.asistenteLucia.PanelAsistenteLucia.deshaciendo', 'Deshaciendo…') : (a.deshacer.boton || i18nT('components.asistenteLucia.PanelAsistenteLucia.deshacer', 'Deshacer'))}
          </button>
          {a.deshacer.quedan_min ? <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{tx('components.asistenteLucia.PanelAsistenteLucia.se_puede_durante_min', 'Se puede durante {{quedan_min}} min', { quedan_min: a.deshacer.quedan_min })}</span> : null}
        </div>
      )}
      {estado === 'deshecha' && <div style={{ marginTop: '6px', color: 'var(--text-muted)' }}>{(a.resultado && a.resultado.deshecho) || i18nT('components.asistenteLucia.PanelAsistenteLucia.deshecho', 'Deshecho.')}</div>}
      {estado === 'descartada' && <div style={{ marginTop: '6px', color: 'var(--text-muted)' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.descartado_no_se_ha_hecho', 'Descartado: no se ha hecho nada.')}</div>}
      {estado === 'caducada' && <div style={{ marginTop: '6px', color: 'var(--text-muted)' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.ha_caducado_sin_hacerse_pideselo', 'Ha caducado sin hacerse: pídeselo otra vez a LucIA.')}</div>}
      {estado === 'fallida' && <div role="alert" style={{ marginTop: '6px', color: 'var(--danger)' }}>{a.error || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_hacer', 'No se ha podido hacer.')}</div>}
      {fallo && <div role="alert" style={{ marginTop: '6px', color: 'var(--danger)' }}>{fallo}</div>}
    </div>
  )
}

function Hora({ hora, clara }) {
  if (!hora) return null
  return <span style={{ fontSize: '10.5px', color: clara ? 'color-mix(in srgb, var(--sobre-primary) 75%, transparent)' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', marginLeft: '10px', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>{hora}</span>
}

function Separador({ texto }) {
  if (!texto) return null
  return (
    <div data-asistente-dia={texto} style={{ display: 'flex', justifyContent: 'center', margin: '2px 0' }}>
      <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '999px', padding: '3px 11px' }}>{texto}</span>
    </div>
  )
}

// Una respuesta de LucIA: su miniatura a la izquierda y la burbuja. t369 · En el móvil, sus tarjetas (`debajo`) van FUERA de
// la burbuja, a todo el ancho de la conversación (dentro se quedaban con lo que dejan la cara y el relleno).
// t452 · Con el panel ampliado (`lectura`), la burbuja va centrada al ancho de lectura y sus tarjetas, debajo, a todo el ancho.
function BurbujaLucia({ children, hora, datos = {}, debajo = null, lectura = null }) {
  const fila = (
    <div data-asistente-mensaje="lucia" {...datos} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', alignSelf: 'stretch', ...(lectura ? enLaColumna(lectura) : {}) }}>
      <CaraLucia lado={30} />
      <div style={{ flex: 1, minWidth: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px 16px 16px 16px', padding: '9px 12px',
        fontSize: '13px', lineHeight: 1.5, boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}>
        {children}
        {hora && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '3px' }}><Hora hora={hora} /></div>}
      </div>
    </div>
  )
  if (!debajo) return fila
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignSelf: 'stretch', minWidth: 0 }}>
      {fila}
      <div data-asistente-a-lo-ancho="" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>{debajo}</div>
    </div>
  )
}

// LOS ACCESOS RÁPIDOS: 2×2 debajo del saludo. Uno con formulario lo abre dentro del chat. (Sin estado propio: lo lleva el panel,
// que es el único que usa hooks.)
function AccesosRapidos({ onPregunta, ocupado, abierto, setAbierto, valor, setValor, movil = false }) {
  const acceso = ACCESOS_RAPIDOS.find((a) => a.id === abierto) || null
  const mandar = () => {
    const v = valor.trim()
    if (!acceso || !acceso.formulario || !v || ocupado) return
    onPregunta(acceso.formulario.pregunta(v))
    setAbierto(null); setValor('')
  }
  return (
    <div data-asistente-accesos="" style={{ marginLeft: movil ? 0 : '38px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
        {ACCESOS_RAPIDOS.map((a) => (
          <button key={a.id} type="button" data-asistente-acceso={a.id} disabled={ocupado}
            onClick={() => { if (a.formulario) { setAbierto(abierto === a.id ? null : a.id); setValor('') } else onPregunta(a.pregunta) }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, textAlign: 'left', border: '1px solid ' + (abierto === a.id ? 'var(--primary)' : 'var(--border)'),
              background: 'var(--surface)', borderRadius: '12px', padding: '9px 10px', cursor: ocupado ? 'default' : 'pointer', color: 'var(--text-primary)', fontSize: '12.5px', fontWeight: 700 }}>
            <span aria-hidden="true" style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'color-mix(in srgb, var(--primary) 12%, var(--surface))', color: 'var(--primary)' }}><Ico n={a.icono} s={15} /></span>
            <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{a.texto}</span>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', display: 'flex' }}><Ico n="chevronR" s={14} /></span>
          </button>
        ))}
      </div>
      {acceso && acceso.formulario && (
        <form data-asistente-formulario={acceso.id} onSubmit={(e) => { e.preventDefault(); mandar() }}
          style={{ display: 'flex', gap: '6px', alignItems: 'center', border: '1px solid var(--border)', borderRadius: '12px', padding: '6px', background: 'var(--surface)' }}>
          <input value={valor} onChange={(e) => setValor(e.target.value.slice(0, 120))} placeholder={acceso.formulario.etiqueta} aria-label={acceso.formulario.etiqueta}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', padding: '5px 6px', color: 'var(--text-primary)', fontFamily: 'inherit' }} />
          <button type="submit" disabled={!valor.trim() || ocupado}
            style={{ border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', borderRadius: '9px', padding: '6px 11px', fontWeight: 700, fontSize: '12.5px', cursor: 'pointer', opacity: valor.trim() ? 1 : 0.55 }}>
            {tDato(acceso.formulario.boton)}
          </button>
          <button type="button" aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.cancelar', 'Cancelar')} onClick={() => { setAbierto(null); setValor('') }}
            style={{ border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: '4px' }}><Ico n="close" s={14} /></button>
        </form>
      )}
    </div>
  )
}

function BotonCabecera({ etiqueta, onClick, children, datos = {}, lado = 34, titulo = null }) {
  return (
    <button type="button" onClick={onClick} title={titulo || etiqueta} aria-label={etiqueta} {...datos}
      style={{ width: lado + 'px', height: lado + 'px', borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0, boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
      {children}
    </button>
  )
}

// t452 · «AMPLIAR» / «VOLVER AL PANEL»: debajo de la cara de LucIA, a la izquierda de la cabecera; solo en el ordenador y en la
// tablet tumbada. Dice lo que hace con su texto y su icono (las cuatro esquinas hacia fuera o hacia dentro), y `aria-expanded`
// dice si está ampliado. El aro del foco es el de toda la aplicación (index.css, :focus-visible).
function BotonAmpliar({ ampliado, onClick }) {
  const etiqueta = ampliado ? i18nT('components.asistenteLucia.PanelAsistenteLucia.volver_al_panel', 'Volver al panel') : i18nT('components.asistenteLucia.PanelAsistenteLucia.ampliar_el_panel_a_todo', 'Ampliar el panel a todo el ancho')
  return (
    <button type="button" data-asistente-ampliar={ampliado ? 'ampliado' : 'panel'} onClick={onClick} aria-expanded={ampliado} aria-controls="asistente-lucia-panel"
      aria-label={etiqueta} title={ampliado ? i18nT('components.asistenteLucia.PanelAsistenteLucia.volver_al_panel_esc', 'Volver al panel (Esc)') : i18nT('components.asistenteLucia.PanelAsistenteLucia.ampliar_a_todo_el_ancho', 'Ampliar a todo el ancho de la pantalla')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 10px 0 8px', borderRadius: '999px', whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
        border: '1px solid ' + (ampliado ? 'color-mix(in srgb, var(--primary) 45%, var(--border))' : 'var(--border)'),
        background: ampliado ? 'color-mix(in srgb, var(--primary) 10%, var(--surface))' : 'var(--surface)', color: ampliado ? 'var(--primary)' : 'var(--text-secondary)',
        fontSize: '12px', fontWeight: 700, fontFamily: 'inherit', boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
        {ampliado
          ? <><polyline points="4 9 9 9 9 4" /><polyline points="15 4 15 9 20 9" /><polyline points="20 15 15 15 15 20" /><polyline points="9 20 9 15 4 15" /></>
          : <><polyline points="9 3.5 3.5 3.5 3.5 9" /><polyline points="15 3.5 20.5 3.5 20.5 9" /><polyline points="20.5 15 20.5 20.5 15 20.5" /><polyline points="3.5 15 3.5 20.5 9 20.5" /></>}
      </svg>
      {ampliado ? i18nT('components.asistenteLucia.PanelAsistenteLucia.volver_al_panel', 'Volver al panel') : i18nT('components.asistenteLucia.PanelAsistenteLucia.ampliar', 'Ampliar')}
    </button>
  )
}

export default function PanelAsistenteLucia() {
  const encendida = useFuncion('asistente-lucia')
  const { user } = useAuth() || {}
  const estrecho = useIsMobile(900)
  const navigate = useNavigate()
  const location = useLocation() || {}
  const branding = useBranding() || {}
  // montado: en la página; visible: dentro (la animación va de uno a otro). Minimizado con «–», se guarda todo (lo escrito
  // también) y no se vuelve a pedir la conversación; cerrado con la «X», lo escrito se va y al abrir se pide otra vez.
  const [montado, setMontado] = useState(false)
  const [visible, setVisible] = useState(false)
  const [mensajes, setMensajes] = useState([])
  const [dias, setDias] = useState({ hoy: null, ayer: null })
  const [texto, setTexto] = useState('')
  const [esperando, setEsperando] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')
  const [diasCaducidad, setDiasCaducidad] = useState(7)
  const [ancho, setAncho] = useState(() => leerAncho(almacen(), user && user.id))
  const [reducido] = useState(() => prefiereMenosMovimiento(ventana()))
  // La voz: la grabadora de la casa (dos minutos como mucho) y si lo que hay en el cuadro vino dictado.
  const grabadora = useGrabadoraNota({ maxSeg: 120, onError: (t) => setError(t) })
  const [transcribiendo, setTranscribiendo] = useState(false)
  const [deVoz, setDeVoz] = useState(false)
  const conVoz = puedeGrabar()
  const finRef = useRef(null)
  const cuadroRef = useRef(null)
  const cerrarTimer = useRef(null)
  const yaCargado = useRef(false)
  const visibleRef = useRef(false)
  visibleRef.current = visible
  // ¿Puede abrirse ahora? (lo mira el aviso del botón de LucIA: si no, ese botón abre el soporte, que es lo suyo)
  const puedeAbrirRef = useRef(false)
  puedeAbrirRef.current = !!(encendida && user)

  // LO QUE TIENE ABIERTO: la dirección y la ventana abierta (una cita, un presupuesto). Solo ids.
  const [abiertoVentana, setAbiertoVentana] = useState(() => abiertoAhora())
  useEffect(() => suscribirAbierto(() => setAbiertoVentana(abiertoAhora())), [])
  // t369 · LO QUE PIDIÓ EL BOTÓN de la ficha, la cita o la agenda: su contexto y su sitio. Manda sobre lo abierto mientras se
  // siga en esa pantalla (utils/asistenteLucia · pantallaConPedido); al irse a otra, deja de valer.
  const [pedido, setPedido] = useState(null)
  const clavePagina = claveDeLaPagina(location.pathname, location.search)
  const clavePaginaRef = useRef(clavePagina)
  clavePaginaRef.current = clavePagina
  const pedidoVale = !!(pedido && pedido.clave === clavePagina)
  const pantalla = pantallaConPedido(pantallaParaLucia(pantallaDeLaDireccion(location.pathname, location.search), abiertoVentana), pedido, clavePagina)
  const clave = clavePantalla(pantalla)
  const hayPantalla = Object.keys(pantalla).length > 0
  const [viendo, setViendo] = useState(null)
  // Las sugerencias de ese sitio (las da el servidor con los permisos de quien pregunta); se apartan al mandar la primera.
  const [sugerencias, setSugerencias] = useState([])
  const [sugerenciasUsadas, setSugerenciasUsadas] = useState(false)
  // t369 · en el móvil: lo que tapa el teclado (y lo que el navegador ha corrido la vista) y el arrastre de la cabecera.
  const [teclado, setTeclado] = useState(SIN_TECLADO)
  const [arrastreY, setArrastreY] = useState(null)
  const gesto = useRef(null)
  const panelRef = useRef(null)
  const atajo = textoAtajoLucia((ventana() || {}).navigator)

  // EL ANCHO GUARDADO ES DE CADA PERSONA: si cambia quien ha entrado, el suyo.
  useEffect(() => { setAncho(leerAncho(almacen(), user && user.id)) }, [user && user.id])   // eslint-disable-line react-hooks/exhaustive-deps

  // t452 · AMPLIADO A TODO EL ANCHO: la última elección de cada persona en este navegador. En el móvil no cuenta (ya es la
  // pantalla entera): `amplio` es lo que se pinta.
  const [ampliado, setAmpliado] = useState(() => leerAmpliado(almacen(), user && user.id))
  useEffect(() => { setAmpliado(leerAmpliado(almacen(), user && user.id)) }, [user && user.id])   // eslint-disable-line react-hooks/exhaustive-deps
  const amplio = ampliado && !estrecho
  // Lo que mide la conversación de ancho (para dibujar la gráfica a su medida): lo mide el navegador con un ResizeObserver al
  // cambiar (arrastrar el borde, ampliar, volver, la ventana); hasta entonces, o sin él, la cuenta con la ventana.
  const anchoVentanaApp = () => { const w = ventana(); const a = w ? Number(w.innerWidth) : 0; return a > 0 ? a / zoomDeLaPagina() : 0 }
  const [anchoVentana, setAnchoVentana] = useState(() => anchoVentanaApp())
  const [anchoMedido, setAnchoMedido] = useState(0)
  const conversacionRef = useRef(null)
  // Al ampliar o volver, lo medido es de antes: se olvida y vale la cuenta hasta que el navegador mida otra vez (así la gráfica
  // no sale un momento estirada a la medida de antes).
  const cambiarAmpliado = (si) => {
    setAmpliado(si === true)
    setAnchoMedido(0)
    guardarAmpliado(almacen(), user && user.id, si === true)
  }

  const abrir = useCallback(() => {
    if (cerrarTimer.current) { clearTimeout(cerrarTimer.current); cerrarTimer.current = null }
    setMontado(true)
    alSiguienteCuadro(() => setVisible(true))
  }, [])
  // Cerrar y minimizar salen igual (con su animación); el foco vuelve al botón de LucIA de la esquina.
  const ocultar = useCallback((comoMinimizar) => {
    if (!visibleRef.current && !cerrarTimer.current) return
    setVisible(false)
    setArrastreY(null)
    if (!comoMinimizar) { setTexto(''); setDeVoz(false); yaCargado.current = false; setPedido(null); setSugerencias([]) }
    if (cerrarTimer.current) clearTimeout(cerrarTimer.current)
    cerrarTimer.current = setTimeout(() => { cerrarTimer.current = null; setMontado(false) }, ANIMACION_PANEL.cerrarMs + 40)
    try {
      const b = typeof document !== 'undefined' && document.querySelector ? document.querySelector('[data-boton-lucia] button, [data-boton-lucia] [role="button"]') : null
      if (b && typeof b.focus === 'function') b.focus({ preventScroll: true })
    } catch (_) { /* sin pantalla */ }
  }, [])
  const cerrar = useCallback(() => ocultar(false), [ocultar])
  const minimizar = useCallback(() => ocultar(true), [ocultar])
  useEffect(() => () => { if (cerrarTimer.current) clearTimeout(cerrarTimer.current) }, [])

  useEffect(() => {
    const alAbrir = (e) => {
      const d = (e && e.detail) || {}
      abrir()
      const p = typeof d.pregunta === 'string' ? d.pregunta : ''
      if (p) setTexto(p)
      // t369 · con su contexto (la ficha, la cita, el hueco): vale para esta pantalla; sus sugerencias, desde cero.
      const ctx = contextoLimpio(d.contexto)
      if (Object.keys(ctx).length) {
        setPedido({ contexto: ctx, clave: clavePaginaRef.current, sitio: SITIOS_LUCIA.includes(d.sitio) ? d.sitio : null })
        setSugerencias([])
        setSugerenciasUsadas(false)
      }
    }
    // El botón de LucIA de la esquina (t340): se atiende su aviso y se abre el panel.
    const delBoton = (e) => {
      if (!puedeAbrirRef.current) return
      try { if (e && e.detail) e.detail.atendido = true } catch (_) { /* sin detalle */ }
      abrir()
    }
    window.addEventListener(EVENTO_ABRIR_ASISTENTE_LUCIA, alAbrir)
    window.addEventListener(EVENTO_CERRAR_ASISTENTE_LUCIA, cerrar)
    window.addEventListener(AVISO_DEL_BOTON_DE_LUCIA, delBoton)
    return () => {
      window.removeEventListener(EVENTO_ABRIR_ASISTENTE_LUCIA, alAbrir)
      window.removeEventListener(EVENTO_CERRAR_ASISTENTE_LUCIA, cerrar)
      window.removeEventListener(AVISO_DEL_BOTON_DE_LUCIA, delBoton)
    }
  }, [abrir, cerrar])

  const cargar = useCallback(async () => {
    setCargando(true)
    setError('')
    try {
      const h = await api.get(BASE + '/historial')
      const d = h.data || {}
      setMensajes(Array.isArray(d.mensajes) ? d.mensajes : [])
      setDias({ hoy: d.hoy || null, ayer: d.ayer || null })
      if (d.dias_caducidad) setDiasCaducidad(d.dias_caducidad)
      yaCargado.current = true
      pedirLoDeHoy()
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_abrir', 'No se ha podido abrir la conversación.'))
    } finally { setCargando(false) }
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  // t368 · LO DE HOY: la primera vez del día que se abre, LucIA saluda con lo importante de hoy (sin IA: lo calcula el servidor,
  // backend/services/asistenteLuciaHoy.js, que también sabe si ya saludó hoy o si esta persona lo tiene apagado).
  const [avisosDia, setAvisosDia] = useState({ apagado: false, cambiando: false })
  const pedirLoDeHoy = async () => {
    try {
      const r = await api.get(BASE + '/avisos-del-dia')
      const d = (r && r.data) || {}
      setAvisosDia((x) => ({ ...x, apagado: d.apagado === true }))
      if (d.mensaje && d.mensaje.id) setMensajes((ms) => (ms.some((x) => x.id === d.mensaje.id) ? ms : [...ms, d.mensaje]))
    } catch (_) { /* sin lo de hoy, el panel sigue igual */ }
  }
  const cambiarAvisosDia = async (si) => {
    setAvisosDia((x) => ({ ...x, cambiando: true }))
    try {
      await api.put(BASE + '/preferencias', { avisos_dia: si === true })
      setAvisosDia({ apagado: si !== true, cambiando: false })
      if (si === true) pedirLoDeHoy()
    } catch (e) {
      setAvisosDia((x) => ({ ...x, cambiando: false }))
      setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_cambiar', 'No se ha podido cambiar.'))
    }
  }

  // Al abrir: la conversación (minimizado, se queda la que había) y el foco al cuadro.
  useEffect(() => {
    if (!montado || !encendida || !user) return
    if (!yaCargado.current) cargar()
  }, [montado, encendida, user, cargar])
  useEffect(() => {
    if (!visible) return
    try { if (cuadroRef.current && cuadroRef.current.focus) cuadroRef.current.focus({ preventScroll: true }) } catch (_) { /* sin pantalla */ }
  }, [visible])
  useEffect(() => { try { if (finRef.current && finRef.current.scrollIntoView) finRef.current.scrollIntoView({ block: 'end' }) } catch (_) { /* sin pantalla */ } }, [mensajes, esperando, teclado.abajo])
  // «Viendo: …»: lo que hay abierto, comprobado por el servidor (lo que no pasa, no se dice).
  useEffect(() => {
    if (!montado || !encendida || !user) return undefined
    if (!hayPantalla) { setViendo(null); return undefined }
    let vivo = true
    api.get(BASE + '/pantalla', { params: pantalla })
      .then((r) => { if (vivo) setViendo((r.data && r.data.texto) || null) })
      .catch(() => { if (vivo) setViendo(null) })
    return () => { vivo = false }
  }, [montado, encendida, user, clave])   // eslint-disable-line react-hooks/exhaustive-deps

  // t369 · LO PEDIDO DEJA DE VALER AL IRSE A OTRA PANTALLA (y sus sugerencias con él).
  useEffect(() => {
    if (pedido && pedido.clave !== clavePagina) { setPedido(null); setSugerencias([]) }
  }, [clavePagina])   // eslint-disable-line react-hooks/exhaustive-deps
  // LAS SUGERENCIAS DE ESE SITIO, del servidor: solo las que esta persona puede pedir (sus permisos), nunca una lista de aquí.
  useEffect(() => {
    if (!pedido || !pedido.sitio || !encendida || !user) return undefined
    let vivo = true
    api.get(BASE + '/sugerencias', { params: { donde: pedido.sitio } })
      .then((r) => {
        if (!vivo) return
        const l = (r.data && Array.isArray(r.data.sugerencias) ? r.data.sugerencias : [])
          .filter((s) => s && typeof s === 'object' && typeof s.texto === 'string' && typeof s.pregunta === 'string' && s.texto.trim() && s.pregunta.trim())
        setSugerencias(l.slice(0, 6))
      })
      .catch(() => { if (vivo) setSugerencias([]) })
    return () => { vivo = false }
  }, [pedido, encendida, user])

  // t369 · EL ATAJO DE TECLADO (Ctrl + Espacio; en un Mac, Opción + L). Cerrado, lo abre (el foco va al cuadro al entrar);
  // abierto y escribiendo en él, lo cierra como «–» (no se pierde nada); abierto y trabajando en otra parte, lleva a escribir.
  // Se escucha en la ventana ANTES que nadie (captura), y la combinación no sigue: ni escribe un espacio ni pulsa un botón.
  const focoDentro = () => {
    try {
      const p = panelRef.current
      const a = typeof document !== 'undefined' ? document.activeElement : null
      return !!(p && a && typeof p.contains === 'function' && p.contains(a))
    } catch (_) { return false }
  }
  const enfocarCuadro = () => { try { if (cuadroRef.current && cuadroRef.current.focus) cuadroRef.current.focus({ preventScroll: true }) } catch (_) { /* sin pantalla */ } }
  useEffect(() => {
    const w = ventana()
    if (!encendida || !user || !w || typeof w.addEventListener !== 'function') return undefined
    const mac = esMac(w.navigator)
    const alPulsar = (e) => {
      if (!esAtajoLucia(e, mac)) return
      try { e.preventDefault(); if (typeof e.stopPropagation === 'function') e.stopPropagation() } catch (_) { /* sin evento de verdad */ }
      if (!visibleRef.current) { abrir(); return }
      if (focoDentro()) minimizar()
      else enfocarCuadro()
    }
    w.addEventListener('keydown', alPulsar, true)
    return () => w.removeEventListener('keydown', alPulsar, true)
  }, [encendida, user, abrir, minimizar])   // eslint-disable-line react-hooks/exhaustive-deps

  // t452 · EL ANCHO DE LA VENTANA (con el tamaño de letra del perfil) y EL DE LA CONVERSACIÓN, medido por el navegador.
  useEffect(() => {
    const w = ventana()
    if (!montado || !w || typeof w.addEventListener !== 'function') return undefined
    const alCambiar = () => { const a = anchoVentanaApp(); setAnchoVentana((x) => (x === a ? x : a)) }
    alCambiar()
    w.addEventListener('resize', alCambiar)
    return () => w.removeEventListener('resize', alCambiar)
  }, [montado])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const w = ventana()
    const nodo = conversacionRef.current
    if (!montado || !w || typeof w.ResizeObserver !== 'function' || !nodo) return undefined
    const ro = new w.ResizeObserver((entradas) => {
      const e = entradas && entradas[0]
      const a = e && e.contentRect ? Math.round(e.contentRect.width) : 0
      if (a > 0) setAnchoMedido((x) => (x === a ? x : a))
    })
    ro.observe(nodo)
    return () => { try { ro.disconnect() } catch (_) { /* ya no está */ } }
  }, [montado])
  // Ampliado, Esc vuelve al panel también con el foco fuera de él (al pulsar en un hueco de la conversación el foco se va a la
  // página). Con el foco en otra cosa (una ventana de la casa encima), Esc es de esa cosa; dentro del panel lo atiende el panel.
  useEffect(() => {
    const w = ventana()
    if (!amplio || !visible || !w || typeof w.addEventListener !== 'function') return undefined
    const alPulsar = (e) => {
      if (!e || e.key !== 'Escape' || e.defaultPrevented) return
      let foco = null
      try { foco = document.activeElement } catch (_) { foco = null }
      try { if (foco && foco !== document.body && foco !== document.documentElement) return } catch (_) { /* sin documento */ }
      cambiarAmpliado(false)
    }
    w.addEventListener('keydown', alPulsar)
    return () => w.removeEventListener('keydown', alPulsar)
  }, [amplio, visible])   // eslint-disable-line react-hooks/exhaustive-deps

  // t369 · EN EL MÓVIL, EL TECLADO: lo que tapa abajo y lo que el navegador ha corrido la vista arriba (utils/tecladoMovil, en px
  // de la aplicación). El panel deja esos dos huecos y ocupa justo lo que se ve: el cuadro de escribir queda encima del teclado.
  useEffect(() => {
    const w = ventana()
    const vv = w && w.visualViewport
    if (!estrecho || !montado || !vv || typeof vv.addEventListener !== 'function') {
      setTeclado((t) => (t.arriba || t.abajo ? SIN_TECLADO : t))
      return undefined
    }
    const medir = () => {
      const n = huecoDelTeclado({ altoVentana: w.innerHeight, altoVisible: vv.height, arribaVisible: vv.offsetTop, escala: vv.scale, zoom: zoomDeLaPagina() }) || SIN_TECLADO
      setTeclado((t) => (t.arriba === n.arriba && t.abajo === n.abajo ? t : n))
    }
    medir()
    vv.addEventListener('resize', medir)
    vv.addEventListener('scroll', medir)
    return () => { vv.removeEventListener('resize', medir); vv.removeEventListener('scroll', medir) }
  }, [estrecho, montado])

  const nota = grabadora.nota
  useEffect(() => {
    if (grabadora.estado !== 'lista' || !nota) return undefined
    let vivo = true
    ;(async () => {
      setTranscribiendo(true)
      setError('')
      try {
        const fd = new FormData()
        fd.append('audio', nota.blob, nota.nombre)
        fd.append('segundos', String(nota.segundos || 0))
        const r = await api.post(BASE + '/transcribir', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        const dicho = String((r.data && r.data.texto) || '').trim()
        if (vivo && dicho) {
          // t430 → t451 · UN «SÍ» (O UN «NO») DICHO A LO QUE LucIA ACABA DE PROPONER, con el cuadro vacío, se manda solo: el «no» la
          // descarta y el «sí» NO hace nada (LucIA contesta que pulse «Hacerlo»: lo hace solo el botón). Cualquier otra cosa dictada va
          // al cuadro, para corregirla antes de mandarla, como siempre.
          if (respuestaCortaLucia(dicho) && propuestaQueEspera(mensajes) && !String(texto || '').trim()) enviar(dicho, { dicha: true })
          else {
            setTexto((x) => ((String(x || '').trim() ? String(x).trim() + ' ' : '') + dicho).slice(0, 2000))
            setDeVoz(true)
          }
        }
      } catch (e) {
        if (vivo) setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_pasar', 'No se ha podido pasar el audio a texto. Vuelve a intentarlo o escríbelo.'))
      } finally {
        if (vivo) setTranscribiendo(false)
        grabadora.descartar()
      }
    })()
    return () => { vivo = false }
  }, [grabadora.estado, nota])   // eslint-disable-line react-hooks/exhaustive-deps
  // Al cerrar o minimizar el panel, el micrófono se suelta (sin mandar nada).
  useEffect(() => { if (!visible && (grabadora.estado === 'grabando' || grabadora.estado === 'pidiendo')) grabadora.cancelar() }, [visible, grabadora.estado])   // eslint-disable-line react-hooks/exhaustive-deps

  const enviar = async (pregunta, { dicha = false } = {}) => {
    // Lo dictado que se manda tal cual o corregido cuenta como «de voz»; una pregunta de un acceso rápido, no. (t430: y el «sí»
    // dictado que se manda solo, `dicha`.)
    const vozDeEsta = (pregunta == null && deVoz) || dicha === true
    const t = String(pregunta == null ? texto : pregunta).trim()
    if (!t || esperando) return
    setError('')
    setEsperando(true)
    setSugerenciasUsadas(true)
    const provisional = { id: 'p' + Date.now(), rol: 'persona', texto: t, dia: dias.hoy, hora: horaAhora(), fuentes: [], acciones: [], provisional: true }
    setMensajes((m) => [...m, provisional])
    if (pregunta == null) { setTexto(''); setDeVoz(false) }
    try {
      const r = await api.post(BASE + '/preguntar', { texto: t, de_voz: vozDeEsta === true, ...(hayPantalla ? { pantalla } : {}) })
      setMensajes((m) => [...m.filter((x) => x.id !== provisional.id), r.data.persona, r.data.lucia].filter(Boolean))
      // t430 → t451 · lo que cambió con un «no» escrito o dicho: la tarjeta de antes pasa a descartada. Un «sí» ya no hace nada (lo
      // hace solo «Hacerlo», t451): una respuesta del chat nunca deja una tarjeta hecha ni deshecha, aunque lo dijera.
      for (const c of (Array.isArray(r.data && r.data.acciones_cambiadas) ? r.data.acciones_cambiadas : [])) {
        if (c && c.id != null && c.estado !== 'hecha' && c.estado !== 'deshecha') {
          ponerAccion(c.id, { estado: c.estado, ...(c.resultado ? { resultado: c.resultado } : {}), ...(c.error ? { error: c.error } : {}),
            deshacer: c.deshacer ? { boton: c.deshacer.boton, quedan_min: c.deshacer.minutos } : null })
        }
      }
      // Y si se habló, LucIA contesta en voz alta: la propuesta en una frase (que pide pulsar «Hacerlo») o su respuesta.
      if (vozDeEsta) leerEnVoz(textoParaLeer(r.data && r.data.lucia), typeof window !== 'undefined' ? window : null)
    } catch (e) {
      setMensajes((m) => m.filter((x) => x.id !== provisional.id))
      setTexto(t)
      if (vozDeEsta) setDeVoz(true)
      setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.lucia_no_ha_podido_contestar', 'LucIA no ha podido contestar ahora mismo. Vuelve a intentarlo.'))
    } finally {
      setEsperando(false)
      try { if (cuadroRef.current) cuadroRef.current.focus() } catch (_) { /* sin pantalla */ }
    }
  }

  // t369 · UNA SUGERENCIA: la que es una pregunta entera se manda; la que hay que acabar («Hazle un presupuesto a este paciente
  // de…») se deja en el cuadro con el cursor al final, para decir el resto (escrito o dictado).
  const usarSugerencia = (s) => {
    if (!s || esperando) return
    if (s.completar) {
      setTexto(String(s.pregunta).slice(0, 2000))
      setDeVoz(false)
      alSiguienteCuadro(() => {
        try {
          const c = cuadroRef.current
          if (c && c.focus) c.focus({ preventScroll: true })
          if (c && typeof c.setSelectionRange === 'function') { const n = String(c.value || '').length; c.setSelectionRange(n, n) }
        } catch (_) { /* sin pantalla */ }
      })
      return
    }
    enviar(s.pregunta)
  }

  // t369 · EN EL MÓVIL, DESLIZAR LA CABECERA HACIA ABAJO lo cierra como «–» (utils/asistenteLucia · gestoCierra): el panel sigue
  // al dedo; si no llega, vuelve a su sitio con su resorte. Los botones de la cabecera no empiezan el gesto.
  const empezarGesto = (e) => {
    if (!estrecho || !e) return
    try { const t = e.target; if (t && typeof t.closest === 'function' && t.closest('button, a, input, textarea')) return } catch (_) { /* sin DOM */ }
    gesto.current = { x: Number(e.clientX) || 0, y: Number(e.clientY) || 0, t: Date.now() }
    try { if (e.currentTarget && e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId) } catch (_) { /* sin captura */ }
  }
  const moverGesto = (e) => {
    const g = gesto.current
    if (!g || !e) return
    const dy = ((Number(e.clientY) || 0) - g.y) / zoomDeLaPagina()
    setArrastreY(dy > 0 ? Math.round(dy) : 0)
  }
  const soltarGesto = (e) => {
    const g = gesto.current
    gesto.current = null
    if (!g) return
    const z = zoomDeLaPagina()
    const dy = (((e && Number(e.clientY)) || g.y) - g.y) / z
    const dx = (((e && Number(e.clientX)) || g.x) - g.x) / z
    setArrastreY(null)
    if (gestoCierra({ dy, dx, ms: Date.now() - g.t })) minimizar()
  }

  const empezarDeNuevo = async () => {
    const ok = await confirmar({ titulo: i18nT('components.asistenteLucia.PanelAsistenteLucia.empezar_de_nuevo', 'Empezar de nuevo'), mensaje: i18nT('components.asistenteLucia.PanelAsistenteLucia.se_borra_tu_conversacion_con', 'Se borra tu conversación con LucIA y lo que tengas a medias con ella. No se puede deshacer.'), textoConfirmar: i18nT('components.asistenteLucia.PanelAsistenteLucia.borrar_la_conversacion', 'Borrar la conversación') })
    if (!ok) return
    try { await api.delete(BASE + '/historial'); setMensajes([]) }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_borrar', 'No se ha podido borrar.')) }
  }

  // «Hacerlo» y «No, gracias»: lo hace el servidor con lo que él guardó; la tarjeta solo cambia de estado con su respuesta.
  const ponerAccion = (id, cambio) => setMensajes((ms) => ms.map((m) => (Array.isArray(m.acciones) && m.acciones.some((x) => x.id === id)
    ? { ...m, acciones: m.acciones.map((x) => (x.id === id ? { ...x, ...cambio } : x)) } : m)))
  // Mientras el servidor contesta, la tarjeta no deja pulsar otra vez (y si falla, lo dice en ella).
  const [accionOcupada, setAccionOcupada] = useState(null)
  const [fallosAccion, setFallosAccion] = useState({})
  // t349 · «Abrir su WhatsApp» (la tarjeta de abrir_conversacion): por paciente, si se está abriendo y, si no se pudo, por qué.
  const [estadoWhatsapp, setEstadoWhatsapp] = useState({})
  // t351 · «He comprobado los datos» de cada tarjeta con candado (una factura), por acción.
  const [comprobados, setComprobados] = useState({})
  const comprobar = (id, si) => setComprobados((c) => ({ ...c, [id]: si === true }))
  // El acceso rápido con su mini formulario abierto, y lo escrito en él.
  const [accesoAbierto, setAccesoAbierto] = useState(null)
  const [accesoValor, setAccesoValor] = useState('')
  const pulsarAccion = async (a, fn) => {
    if (accionOcupada) return
    setAccionOcupada(a.id)
    setFallosAccion((f) => ({ ...f, [a.id]: '' }))
    try { await fn() }
    catch (e) {
      // t472 · SIN RESPUESTA DEL SERVIDOR (la red se cortó, el navegador se rindió a medias): no se sabe si llegó a hacerse. Se dice
      // en llano y se vuelve a pedir la conversación, que trae cada tarjeta con su estado de verdad (hecha, propuesta, fallida):
      // así no se pulsa «Hacerlo» otra vez sobre algo que ya está hecho creyendo que falló.
      const sinRespuesta = !(e && e.response)
      setFallosAccion((f) => ({ ...f, [a.id]: sinRespuesta
        ? i18nT('components.asistenteLucia.PanelAsistenteLucia.se_ha_cortado_la_conexion', 'Se ha cortado la conexión y no sé si ha llegado a hacerse: vuelvo a cargar la conversación para ver cómo ha quedado.')
        : ((e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_hacer', 'No se ha podido hacer.')) }))
      if (sinRespuesta) cargar().catch(() => {})
    }
    finally { setAccionOcupada(null) }
  }
  const hacerAccion = (a, cuerpo = {}) => pulsarAccion(a, async () => {
    try {
      const r = await api.post(BASE + '/acciones/' + a.id + '/hacer', cuerpo && cuerpo.comprobado === true ? { comprobado: true } : {})
      // t368 · una cadena que se para en el primer paso vuelve 'fallida' (con lo que pasó en cada paso), no 'hecha'.
      ponerAccion(a.id, { estado: (r.data && r.data.estado) || 'hecha', resultado: r.data && r.data.resultado, ...(r.data && r.data.error ? { error: r.data.error } : {}),
        deshacer: r.data && r.data.deshacer ? { boton: r.data.deshacer.boton, quedan_min: r.data.deshacer.minutos } : null })
    } catch (e) {
      const codigo = e && e.response && e.response.data && e.response.data.codigo
      if (codigo === 'YA_HECHA') ponerAccion(a.id, { estado: 'hecha' })
      else if (codigo === 'CADUCADA') ponerAccion(a.id, { estado: 'caducada' })
      throw e
    }
  })
  // t370 · fase 7b · «Ofrecérselo al paciente»: la misma tarjeta pasa a ser la oferta y se le manda (el servidor la prepara y la hace).
  const ofrecerAccion = (a) => pulsarAccion(a, async () => {
    const r = await api.post(BASE + '/acciones/' + a.id + '/ofrecer', {})
    ponerAccion(a.id, { estado: (r.data && r.data.estado) || 'hecha', resultado: r.data && r.data.resultado, tarjeta: (r.data && r.data.tarjeta) || a.tarjeta,
      tipo: (r.data && r.data.tipo) || a.tipo, deshacer: null })
  })
  const descartarAccion = (a) => pulsarAccion(a, async () => {
    await api.post(BASE + '/acciones/' + a.id + '/descartar', {})
    ponerAccion(a.id, { estado: 'descartada' })
  })
  // «Deshacer»: lo hace el servidor (solo lo de esa persona, dentro de su rato); la tarjeta pasa a deshecha con su respuesta.
  const deshacerAccion = (a) => pulsarAccion(a, async () => {
    try {
      const r = await api.post(BASE + '/acciones/' + a.id + '/deshacer', {})
      ponerAccion(a.id, { estado: 'deshecha', resultado: r.data && r.data.resultado, deshacer: null })
    } catch (e) {
      const codigo = e && e.response && e.response.data && e.response.data.codigo
      if (codigo === 'YA_DESHECHA') ponerAccion(a.id, { estado: 'deshecha', deshacer: null })
      else if (codigo === 'PLAZO' || codigo === 'NO_SE_DESHACE') ponerAccion(a.id, { deshacer: null })
      throw e
    }
  })

  // EL INFORME A FICHERO (t345): lo MISMO que «Descargar» de la pantalla de Informes. Se le pide a la misma ruta
  // (POST /informes/ejecutar, con la sesión de esta persona: su permiso y su veto por rol) lo que guardó la tarjeta, y se baja
  // con el mismo generador (utils/informesExport): el PDF por la impresión del navegador y el Excel como CSV.
  const descargarInforme = async (t, d) => {
    setError('')
    try {
      // t370 · la de una búsqueda de pacientes (buscar_pacientes_por_criterios) se pide a su ruta del asistente, con sus criterios.
      // t374 · la de una cifra de analizar, a la suya (el mismo cálculo otra vez, con lo pedido: la métrica, el periodo, el desglose).
      // t505 · la de un informe a medida, a la ruta de exportar del Informe avanzado (lo saca otra vez y lo apunta en el Registro).
      const ruta = t.peticion_de === 'cruce' ? BASE + '/cruces/ejecutar' : (t.peticion_de === 'metricas' ? BASE + '/metricas/ejecutar' : (t.peticion_de === 'avanzado' ? '/informes/avanzado/exportar' : '/informes/ejecutar'))
      const r = await api.post(ruta, t.peticion_de === 'avanzado' ? { peticion: t.peticion, formato: d.formato === 'pdf' ? 'pdf' : 'excel', desde_lucia: true } : t.peticion)
      const res = r.data || {}
      const columnas = (res.columnas || []).filter((c) => !c.oculta_defecto)
      const filas = res.filas || []
      if (!filas.length) { setError(i18nT('components.asistenteLucia.PanelAsistenteLucia.ese_informe_no_tiene_filas', 'Ese informe no tiene filas para esas fechas.')); return }
      const nombre = res.nombre || t.titulo
      const rango = t.periodo || ''
      if (d.formato === 'pdf') {
        const bloques = [{ columnas, filas, totales: res.totales || {}, total_principal: res.total_principal || null }]
        if (!imprimirInformePDF({ nombre, clinica: branding.nombre || '', rangoLabel: rango, bloques })) setError(i18nT('components.asistenteLucia.PanelAsistenteLucia.permite_las_ventanas_emergentes_para', 'Permite las ventanas emergentes para generar el PDF.'))
      } else descargarCSV(nombre, columnas, filas, rango)
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_bajar', 'No se ha podido bajar el informe.'))
    }
  }

  // t505 · «USAR ESTA LISTA EN UNA CAMPAÑA»: lo prepara el servidor (sin el modelo, sin coste) y vuelve un mensaje de LucIA con la
  // tarjeta de «Hacerlo» (la campaña, en borrador, solo se crea al pulsarlo).
  const [estadoCampana, setEstadoCampana] = useState({})
  const usarEnCampana = async (t) => {
    const k = claveCampana(t)
    setError('')
    setEstadoCampana((e) => ({ ...e, [k]: { pidiendo: true, error: '' } }))
    let error = ''
    try {
      const r = await api.post(BASE + '/informes/campana', { peticion: t.peticion })
      const m = r.data && r.data.mensaje
      if (m && m.id) setMensajes((ms) => (ms.some((x) => x.id === m.id) ? ms : [...ms, m]))
    } catch (e) { error = (e && e.response && e.response.data && e.response.data.error) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_preparar', 'No se ha podido preparar la campaña.') }
    setEstadoCampana((e) => ({ ...e, [k]: { pidiendo: false, error } }))
  }

  // «Ver ficha», «Ver cita»…: se navega SIN cerrar el panel (se trabaja con los dos). En el móvil, que lo tapa todo, se minimiza.
  // t452 · Ampliado (también lo tapa todo), vuelve al panel: se ve la pantalla con LucIA al lado.
  const irA = (ruta) => {
    navigate(ruta)
    avisarDelEnlace(ventana())
    if (estrecho) minimizar()
    else if (amplio) cambiarAmpliado(false)
  }
  // t349 · «Abrir su WhatsApp»: la función común de todos los botones de WhatsApp de un paciente, que navega con `irA`.
  const abrirWhatsapp = async (t) => {
    const pid = t && (t.paciente_id || t.id)
    if (!pid) return
    setEstadoWhatsapp((e) => ({ ...e, [pid]: { abriendo: true, motivo: '' } }))
    let r = null
    try { r = await abrirWhatsAppPaciente({ api, msgError, pacienteId: pid, nombre: t.nombre, navegar: irA }) } catch (_) { r = null }
    setEstadoWhatsapp((e) => ({ ...e, [pid]: { abriendo: false, motivo: r && r.ok ? '' : ((r && r.motivo) || i18nT('components.asistenteLucia.PanelAsistenteLucia.no_se_ha_podido_abrir_la', 'No se ha podido abrir la conversación')) } }))
  }

  // EL ANCHO, ARRASTRANDO EL BORDE IZQUIERDO (o con las flechas en él). Se guarda al soltar.
  const arrastre = useRef(null)
  const empezarArrastre = (e) => {
    if (estrecho) return
    const w = ventana()
    if (!w) return
    try { e.preventDefault() } catch (_) { /* sin evento de verdad */ }
    arrastre.current = { x: e.clientX, ancho }
    let ultimo = ancho
    const mover = (ev) => { if (!arrastre.current) return; ultimo = anchoValido(arrastre.current.ancho + (arrastre.current.x - ev.clientX)); setAncho(ultimo) }
    const soltar = () => {
      arrastre.current = null
      w.removeEventListener('pointermove', mover)
      w.removeEventListener('pointerup', soltar)
      guardarAncho(almacen(), user && user.id, ultimo)
    }
    w.addEventListener('pointermove', mover)
    w.addEventListener('pointerup', soltar)
  }
  const teclaBorde = (e) => {
    const d = e.key === 'ArrowLeft' ? ANCHO_PANEL.paso : e.key === 'ArrowRight' ? -ANCHO_PANEL.paso : 0
    if (!d) return
    e.preventDefault()
    const n = anchoValido(ancho + d)
    setAncho(n)
    guardarAncho(almacen(), user && user.id, n)
  }

  if (!encendida || !user || !montado) return null

  const animBase = estiloAnimacion({ visible, movil: estrecho, reducido })
  // t369 · arrastrando la cabecera en el móvil, el panel sigue al dedo (sin transición); al soltar, la de siempre.
  const anim = estrecho && arrastreY != null ? { ...animBase, transform: 'translate3d(0, ' + arrastreY + 'px, 0)', transition: 'none' } : animBase
  // PEGADO A LA DERECHA (en el móvil, a pantalla completa). Sin fondo que tape nada: solo ocupa su sitio. Las medidas de la
  // ventana van divididas por el tamaño del perfil (--z) y cuentan las barras del sistema (el área segura, index.css).
  // t369 · En el móvil, con el teclado abierto, deja arriba lo que el navegador ha corrido la vista y abajo el teclado (y entonces
  // sin la zona segura de abajo, que queda debajo del teclado); y a los lados, las zonas seguras del móvil tumbado.
  // t452 · AMPLIADO: la ventana entera menos su margen por los cuatro lados (y las zonas seguras de una tablet), con los cuatro
  // bordes redondeados. Sin ancho escrito: lo dan la izquierda y la derecha, así que nunca es más ancho que la ventana.
  const M = PANEL_AMPLIADO.margen
  const lateral = { top: 0, right: 0, bottom: 0, width: 'min(' + ancho + 'px, calc(100vw / var(--z, 1)))', borderRadius: '22px 0 0 22px',
    boxShadow: '-10px 0 36px rgba(16,24,40,0.16)', borderLeft: '1px solid var(--border)', paddingTop: 'var(--sa-arriba, 0px)', paddingBottom: 'var(--sa-abajo, 0px)' }
  const ampliadoCaja = { top: M + 'px', bottom: M + 'px', left: 'calc(' + M + 'px + var(--sa-izq, 0px))', right: 'calc(' + M + 'px + var(--sa-der, 0px))', borderRadius: '22px',
    boxShadow: '0 18px 60px rgba(16,24,40,0.22)', border: '1px solid var(--border)', paddingTop: 'var(--sa-arriba, 0px)', paddingBottom: 'var(--sa-abajo, 0px)' }
  const caja = estrecho
    ? { position: 'fixed', top: teclado.arriba ? teclado.arriba + 'px' : 0, left: 0, right: 0, bottom: teclado.abajo ? teclado.abajo + 'px' : 0, boxSizing: 'border-box',
      paddingTop: 'var(--sa-arriba, 0px)', paddingBottom: teclado.abajo ? 0 : 'var(--sa-abajo, 0px)', paddingLeft: 'var(--sa-izq, 0px)', paddingRight: 'var(--sa-der, 0px)' }
    : { position: 'fixed', ...(amplio ? ampliadoCaja : lateral) }
  // t452 · La columna de lectura (ampliado) y el ancho que tienen las tarjetas de una respuesta (para la gráfica).
  const lectura = amplio ? PANEL_AMPLIADO.lectura : null
  const anchoConversacion = anchoMedido || anchoConversacionCalculado({ anchoVentana, anchoPanel: ancho, ampliado: amplio, movil: estrecho })
  const anchoTarjetas = anchoDeLasTarjetas({ anchoConversacion, ampliado: amplio, movil: estrecho })
  // Lo de abajo, ampliado: centrado y del ancho de la columna de lectura (la barra de escribir, con su relleno de 14 a cada lado). Se
  // cambia el margen entero (arriba, centrado, abajo) y no solo el de los lados, para no mezclar `margin` con `marginLeft`.
  const alPie = (estilo, barra = false) => {
    if (!amplio) return estilo
    const m = String(estilo.margin || '0').split(/\s+/)
    const arriba = m[0] || '0'
    const abajo = m.length >= 3 ? m[2] : arriba
    return { ...estilo, margin: arriba + ' auto ' + abajo, width: barra ? '100%' : 'calc(100% - 32px)', maxWidth: (PANEL_AMPLIADO.lectura + (barra ? 28 : 0)) + 'px', boxSizing: 'border-box' }
  }
  // El día de lo que hay: «Hoy» va encima del saludo si la conversación es de hoy (o no hay); si no, cada día con lo suyo.
  const primeraDeHoy = !mensajes.length || (mensajes[0] && mensajes[0].dia === dias.hoy)
  let diaAnterior = primeraDeHoy ? dias.hoy : null
  // t369 · la pista del cuadro y las sugerencias, las del sitio desde el que se abrió (mientras valga).
  const pista = (pedidoVale && pedido.sitio && PISTA_POR_SITIO[pedido.sitio]) || tDato(PISTA_DE_SIEMPRE)
  const verSugerencias = pedidoVale && sugerencias.length > 0 && !sugerenciasUsadas && !texto.trim() && !esperando

  return (
    <aside ref={panelRef} id="asistente-lucia-panel" data-asistente-lucia="" role="complementary" aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.asistente_lucia', 'Asistente LucIA')} data-visible={visible ? 'si' : 'no'}
      data-teclado={estrecho && teclado.abajo ? 'abierto' : undefined} data-ampliado={amplio ? 'si' : undefined}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); if (amplio) cambiarAmpliado(false); else cerrar() } }}
      style={{ ...caja, ...anim, zIndex: 'var(--capa-aviso-flotante)', background: 'var(--bg, var(--surface))', display: 'flex', flexDirection: 'column', color: 'var(--text-primary)',
        overflow: 'hidden', willChange: 'transform, opacity', pointerEvents: visible ? 'auto' : 'none' }}>
      {!estrecho && !amplio && (
        <div data-asistente-borde="" role="separator" aria-orientation="vertical" aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.cambiar_el_ancho_del_panel', 'Cambiar el ancho del panel')} tabIndex={0}
          aria-valuemin={ANCHO_PANEL.min} aria-valuemax={ANCHO_PANEL.max} aria-valuenow={ancho}
          onPointerDown={empezarArrastre} onKeyDown={teclaBorde}
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 2, touchAction: 'none' }} />
      )}

      {/* LA CABECERA: la cara de LucIA, quién es y la foto de la consulta fundida a la derecha (decorativa). t369 · En el móvil,
          su asa arriba y el gesto: deslizarla hacia abajo cierra el panel (como «–»). */}
      <div data-asistente-cabecera="" data-asistente-gesto={estrecho ? 'abajo' : undefined}
        onPointerDown={estrecho ? empezarGesto : undefined} onPointerMove={estrecho ? moverGesto : undefined}
        onPointerUp={estrecho ? soltarGesto : undefined} onPointerCancel={estrecho ? () => { gesto.current = null; setArrastreY(null) } : undefined}
        style={{ position: 'relative', flexShrink: 0, padding: estrecho ? '18px 14px 12px' : '16px 16px 14px', borderBottom: '1px solid var(--border)',
          background: 'color-mix(in srgb, var(--primary) 5%, var(--surface))', touchAction: estrecho ? 'none' : undefined }}>
        {estrecho && <span data-asistente-asa="" aria-hidden="true" style={{ position: 'absolute', top: '6px', left: '50%', transform: 'translateX(-50%)', width: '40px', height: '4px', borderRadius: '2px', background: 'color-mix(in srgb, var(--text-muted) 45%, transparent)', zIndex: 1 }} />}
        <div aria-hidden="true" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: amplio ? 'min(58%, 620px)' : '58%', overflow: 'hidden', pointerEvents: 'none', borderTopLeftRadius: estrecho ? 0 : '22px' }}>
          <img className="ficha-foto-cabecera" src="/ficha/consulta-luminosa.webp" alt="" decoding="async" loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, color-mix(in srgb, var(--primary) 5%, var(--surface)) 0%, color-mix(in srgb, color-mix(in srgb, var(--primary) 5%, var(--surface)) 70%, transparent) 45%, color-mix(in srgb, color-mix(in srgb, var(--primary) 5%, var(--surface)) 25%, transparent) 100%)' }} />
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: estrecho ? '10px' : '12px' }}>
          {/* t452 · en el ordenador y la tablet tumbada, debajo de la cara: «Ampliar» (o «Volver al panel»). En el móvil, no. */}
          {estrecho
            ? <CaraLucia lado={48} />
            : (
              <div data-asistente-cara-y-ampliar="" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <CaraLucia lado={58} />
                <BotonAmpliar ampliado={amplio} onClick={() => cambiarAmpliado(!amplio)} />
              </div>
            )}
          <div style={{ flex: '1 1 120px', minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '19px', lineHeight: 1.2 }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.lucia', 'LucIA')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success, #16a34a)', flexShrink: 0 }} />
              {i18nT('components.asistenteLucia.PanelAsistenteLucia.asistente_de_la_clinica', 'Asistente de la clínica')}
            </div>
            {/* t369 · en el móvil, sin el lema: la cabecera ocupa menos y la conversación se ve más (con el teclado abierto, sobre todo). */}
            {!estrecho && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px', maxWidth: '260px', lineHeight: 1.45 }}>{tDato(LEMA_LUCIA)}</div>}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {mensajes.length > 0 && (
              <BotonCabecera etiqueta={i18nT('components.asistenteLucia.PanelAsistenteLucia.empezar_de_nuevo_borra_la', 'Empezar de nuevo (borra la conversación y lo que tengas a medias)')} onClick={empezarDeNuevo} datos={{ 'data-asistente-empezar': '' }} lado={estrecho ? 40 : 34}><Ico n="refresh" s={15} /></BotonCabecera>
            )}
            <BotonCabecera etiqueta={i18nT('components.asistenteLucia.PanelAsistenteLucia.minimizar', 'Minimizar')} onClick={minimizar} datos={{ 'data-asistente-minimizar': '' }} lado={estrecho ? 40 : 34} titulo={estrecho ? null : i18nT('components.asistenteLucia.PanelAsistenteLucia.minimizar_2', 'Minimizar ({{atajo}})', { atajo })}>
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="12" x2="18" y2="12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
            </BotonCabecera>
            <BotonCabecera etiqueta={i18nT('components.asistenteLucia.PanelAsistenteLucia.cerrar', 'Cerrar')} onClick={cerrar} datos={{ 'data-asistente-cerrar': '' }} lado={estrecho ? 40 : 34}><Ico n="close" s={16} /></BotonCabecera>
          </div>
        </div>
      </div>

      {/* LA CONVERSACIÓN */}
      <div ref={conversacionRef} data-asistente-conversacion="" style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', padding: estrecho ? '12px 12px 10px' : (amplio ? '18px 24px 12px' : '14px 14px 10px'), display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {primeraDeHoy && <Separador texto={etiquetaDia(dias.hoy, dias) || i18nT('components.asistenteLucia.PanelAsistenteLucia.hoy', 'Hoy')} />}
        <BurbujaLucia datos={{ 'data-asistente-bienvenida': '' }} lectura={lectura}>
          <div>{tDato(SALUDO_LUCIA)}</div>
          <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-muted)' }}>
            {tx('components.asistenteLucia.PanelAsistenteLucia.contesto_con_tus_permisos_lo', 'Contesto con tus permisos: lo que no ves en Biango, tampoco te lo digo. Tu conversación es solo tuya y se borra sola a los {{diasCaducidad}} días.', { diasCaducidad })}
          </div>
          {/* t451 · lo que recuerda LucIA (el dueño, 24-oct): los últimos mensajes y lo que tenga a medias; «Empezar de nuevo» lo borra. */}
          <div data-asistente-recuerda="" style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-muted)' }}>
            {i18nT('components.asistenteLucia.PanelAsistenteLucia.lucia_recuerda_los_ultimos_mensajes', 'LucIA recuerda los últimos mensajes y lo que tengas a medias. ')}<b>{i18nT('components.asistenteLucia.PanelAsistenteLucia.empezar_de_nuevo', 'Empezar de nuevo')}</b>{i18nT('components.asistenteLucia.PanelAsistenteLucia.la_flecha_de_arriba_lo', ' (la flecha de arriba) lo borra.')}
          </div>
          {/* t369 · el atajo, dicho donde se ve (en el ordenador; en el móvil no hay teclado de atajos). */}
          {!estrecho && <div data-asistente-atajo="" style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-muted)' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.abreme_y_cierrame_cuando_quieras', 'Ábreme y ciérrame cuando quieras con ')}<b>{atajo}</b>.</div>}
          {avisosDia.apagado && <AvisosDiaPie apagado ocupado={avisosDia.cambiando} onCambiar={cambiarAvisosDia} />}
        </BurbujaLucia>
        <EnColumna lectura={lectura}>
          <AccesosRapidos onPregunta={(t) => enviar(t)} ocupado={esperando} abierto={accesoAbierto} setAbierto={setAccesoAbierto} valor={accesoValor} setValor={setAccesoValor} movil={estrecho} />
        </EnColumna>
        {cargando && <EnColumna lectura={lectura}><div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.abriendo_tu_conversacion', 'Abriendo tu conversación…')}</div></EnColumna>}
        {mensajes.map((m) => {
          const dia = m.dia || null
          const sep = dia && dia !== diaAnterior ? etiquetaDia(dia, dias) : ''
          if (dia) diaAnterior = dia
          const hora = m.hora || (m.cuando ? String(m.cuando).slice(-5) : '')
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sep && <Separador texto={sep} />}
              {m.rol === 'persona'
                ? (
                  <EnColumna lectura={lectura}>
                    <div data-asistente-mensaje="persona" style={{ alignSelf: 'flex-end', maxWidth: '85%', background: 'var(--primary)', color: 'var(--sobre-primary)', borderRadius: '16px 16px 4px 16px',
                      padding: '8px 12px', fontSize: '13px', display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', opacity: m.provisional ? 0.75 : 1 }}>
                      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 0 }}>{m.texto}</span>
                      <Hora hora={hora} clara />
                    </div>
                  </EnColumna>
                )
                : (() => {
                  // Las tarjetas de lo que dio Biango y de lo preparado: dentro de la burbuja en el ordenador; en el móvil, debajo y a
                  // todo el ancho (t369). El pie del saludo del día (t368) va entre las dos, como estaba.
                  // t452 · Ampliado, también debajo: lo de Biango a todo el ancho (lo ancho) o en columnas de lectura; el pie del día y lo
                  // preparado («Hacerlo»), en la columna de lectura.
                  const tarjetas = [
                    ...(m.fuentes || []).map((f, i) => <Fuente key={'f' + i} f={f} onEnlace={irA} onPregunta={(t) => enviar(t)} onDescargar={descargarInforme} onCampana={usarEnCampana} estadoCampana={estadoCampana} ocupado={esperando} onWhatsapp={abrirWhatsapp} estadoWhatsapp={estadoWhatsapp} ancho={anchoTarjetas} lectura={lectura} />),
                    ...(esSaludoDelDia(m) && !avisosDia.apagado ? [<EnColumna key="pie-del-dia" lectura={lectura}><AvisosDiaPie apagado={false} ocupado={avisosDia.cambiando} onCambiar={cambiarAvisosDia} /></EnColumna>] : []),
                    ...(m.acciones || []).map((a) => <EnColumna key={'a' + a.id} lectura={lectura}><TarjetaAccion a={a} ocupado={accionOcupada === a.id} fallo={fallosAccion[a.id] || ''} onHacer={hacerAccion} onDescartar={descartarAccion} onDeshacer={deshacerAccion} onEnlace={irA} comprobado={comprobados[a.id] === true} onComprobar={comprobar} onOfrecer={ofrecerAccion} /></EnColumna>),
                  ]
                  const fuera = (estrecho || amplio) && tarjetas.length > 0
                  return (
                    <BurbujaLucia hora={hora} debajo={fuera ? tarjetas : null} lectura={lectura}>
                      <Texto texto={m.texto} onEnlace={irA} />
                      {!fuera && tarjetas}
                    </BurbujaLucia>
                  )
                })()}
            </div>
          )
        })}
        {esperando && <EnColumna lectura={lectura}><div data-asistente-pensando="" style={{ alignSelf: 'flex-start', marginLeft: estrecho ? '4px' : '38px', color: 'var(--text-muted)', fontSize: '12.5px' }}>{i18nT('components.asistenteLucia.PanelAsistenteLucia.lucia_esta_mirandolo', 'LucIA está mirándolo…')}</div></EnColumna>}
        <div ref={finRef} />
      </div>

      {/* LO DE ABAJO (la voz, lo que tienes abierto, un error, las sugerencias y la barra de escribir). t452 · Ampliado, cada cosa
          centrada al ancho de lectura, como la conversación (`alPie`): el micrófono y su aviso, donde se escribe, sin nada encima. */}
      {(transcribiendo || deVoz) && (
        <div data-asistente-voz-aviso="" style={alPie({ margin: '0 16px 6px', fontSize: '12px', color: 'var(--text-muted)' })}>
          {transcribiendo ? i18nT('components.asistenteLucia.PanelAsistenteLucia.pasando_tu_audio_a_texto', 'Pasando tu audio a texto…') : i18nT('components.asistenteLucia.PanelAsistenteLucia.revisa_el_texto_dictado_y', 'Revisa el texto dictado y corrígelo si hace falta antes de mandarlo. El audio no se guarda.')}
        </div>
      )}
      {viendo && (
        <div data-asistente-viendo="" title={i18nT('components.asistenteLucia.PanelAsistenteLucia.lucia_sabe_lo_que_tienes', 'LucIA sabe lo que tienes abierto: pregúntale por «este paciente» o «esta cita»')}
          style={alPie({ margin: '0 16px 4px', fontSize: '11.5px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 })}>
          <Ico n="eye" s={12} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{viendo}</span>
        </div>
      )}
      {error && <div role="alert" style={alPie({ margin: '0 16px 8px', padding: '8px 10px', borderRadius: '10px', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', color: 'var(--danger)', fontSize: '12.5px' })}>{error}</div>}
      {/* t369 · LAS SUGERENCIAS DEL SITIO desde el que se abrió (la ficha, la cita, el hueco), las que el servidor dice que esta
          persona puede pedir. Hasta que se manda la primera pregunta o se empieza a escribir. */}
      {verSugerencias && (
        <div data-asistente-sugerencias={pedido.sitio} style={alPie({ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '2px 14px 4px', minWidth: 0 })}>
          {sugerencias.map((s) => (
            <button key={s.texto} type="button" data-asistente-sugerencia={s.completar ? 'completar' : 'preguntar'} onClick={() => usarSugerencia(s)}
              style={{ maxWidth: '100%', minHeight: estrecho ? '40px' : '30px', padding: estrecho ? '6px 13px' : '4px 11px', borderRadius: '999px', textAlign: 'left',
                border: '1px solid color-mix(in srgb, var(--primary) 35%, var(--border))', background: 'color-mix(in srgb, var(--primary) 7%, var(--surface))',
                color: 'var(--text-primary)', fontSize: estrecho ? '13.5px' : '12.5px', fontWeight: 700, cursor: 'pointer', overflowWrap: 'anywhere', fontFamily: 'inherit' }}>
              {s.texto}{s.completar ? '…' : ''}
            </button>
          ))}
        </div>
      )}

      {/* LA BARRA DE ESCRIBIR: el cuadro con el micrófono dentro y el botón de enviar, redondo, en el color de la clínica.
          (El clip de adjuntar irá a la izquierda del cuadro el día que LucIA sepa leer un adjunto: hoy no hay botón de mentira.) */}
      <div data-asistente-escribir="" style={alPie({ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: estrecho ? '8px 12px 10px' : '10px 14px 14px', flexShrink: 0 }, true)}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: '4px', border: '1px solid var(--border)', borderRadius: '24px', background: 'var(--surface)',
          padding: '4px 6px 4px 14px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' }}>
          <textarea ref={cuadroRef} value={texto} onChange={(e) => { const v = e.target.value.slice(0, 2000); setTexto(v); if (!v.trim()) setDeVoz(false) }} rows={1}
            placeholder={pista} aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.escribe_tu_consulta', 'Escribe tu consulta')}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar() } }}
            disabled={esperando}
            style={{ flex: 1, minWidth: 0, resize: 'none', border: 'none', outline: 'none', padding: estrecho ? '11px 0' : '9px 0', fontSize: estrecho ? '16px' : '13.5px', fontFamily: 'inherit', background: 'transparent',
              color: 'var(--text-primary)', minHeight: estrecho ? '44px' : '38px', maxHeight: estrecho ? '120px' : '140px' }} />
          {conVoz && (
            grabadora.estado === 'grabando'
              ? (
                <button type="button" data-asistente-microfono="grabando" onClick={() => grabadora.parar()} title={i18nT('components.asistenteLucia.PanelAsistenteLucia.parar_y_pasar_a_texto', 'Parar y pasar a texto')} aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.parar_la_grabacion', 'Parar la grabación')}
                  style={{ height: estrecho ? '44px' : '36px', minWidth: estrecho ? '44px' : '36px', padding: '0 10px', borderRadius: estrecho ? '22px' : '18px', border: 'none', background: 'var(--danger)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', flexShrink: 0, fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  <Ico n="stop" s={15} /> {Math.floor(grabadora.segundos / 60)}:{String(grabadora.segundos % 60).padStart(2, '0')}
                </button>
              )
              : (
                <button type="button" data-asistente-microfono="" onClick={() => grabadora.empezar()} disabled={esperando || transcribiendo || grabadora.estado === 'pidiendo'}
                  title={transcribiendo ? i18nT('components.asistenteLucia.PanelAsistenteLucia.pasando_el_audio_a_texto', 'Pasando el audio a texto…') : i18nT('components.asistenteLucia.PanelAsistenteLucia.hablar_graba_tu_pregunta_y', 'Hablar: graba tu pregunta y te la escribo para que la revises')} aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.hablar', 'Hablar')}
                  style={{ width: estrecho ? '44px' : '36px', height: estrecho ? '44px' : '36px', borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: esperando || transcribiendo ? 'default' : 'pointer', opacity: esperando || transcribiendo ? 0.55 : 1, flexShrink: 0, padding: 0 }}>
                  <Ico n="micro" s={estrecho ? 22 : 18} />
                </button>
              )
          )}
        </div>
        <button type="button" onClick={() => enviar()} disabled={esperando || transcribiendo || !texto.trim()} title={i18nT('components.asistenteLucia.PanelAsistenteLucia.enviar', 'Enviar')} aria-label={i18nT('components.asistenteLucia.PanelAsistenteLucia.enviar', 'Enviar')}
          style={{ width: estrecho ? '50px' : '46px', height: estrecho ? '50px' : '46px', borderRadius: '50%', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: esperando || !texto.trim() ? 'default' : 'pointer', opacity: esperando || !texto.trim() ? 0.6 : 1, flexShrink: 0, padding: 0, boxShadow: '0 2px 6px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
          <Ico n="send" s={18} />
        </button>
      </div>
    </aside>
  )
}
